import { createI18n } from 'vue-i18n';

const messages = {
  en: {
    '10': '10',
    '25': '25',
    '50': '50',
    '100': '100',
    common: {
      client: {
        title: 'Client',
      },
    },
    notifications: {
      title: 'Notifications',
      markAllAsRead: 'Clear',
      noNotifications: 'No new notifications',
    },
    $vuetify: {
      diagnostico: {
        title: 'Diagnosis',
        subtitle: 'Understand automatically where your cash went in the selected period.',
        email: {
          button: 'Send by e-mail',
          title: 'Send by e-mail',
          subtitle: 'Send this diagnosis to one or more recipients.',
          recipients: 'Recipients',
          // eslint-disable-next-line @stylistic/quotes -- {'@'} is vue-i18n's literal escape;
          // a bare @ starts a linked message and throws "Invalid linked format" at parse time.
          'recipients-placeholder': "name{'@'}company.com",
          'recipients-hint': 'Press Enter to add each address.',
          message: 'Message (optional)',
          'message-placeholder': 'Write a note to go with the diagnosis...',
          send: 'Send',
          cancel: 'Cancel',
          done: 'Done',
          'sent-title': 'Diagnosis sent',
          'sent-body': 'Sent to {0} recipient(s).',
          'error-empty': 'Add at least one recipient.',
          'error-invalid': 'Invalid e-mail: {0}',
          'error-failed': 'Could not send. Try again.',
        },
        history: {
          title: 'Previous diagnoses',
          subtitle: 'Every diagnosis you generate is kept here. Click one to reopen it.',
          open: 'Previous',
          close: 'Close history',
          empty: 'You have not generated any diagnosis for this client yet.',
        },
        types: {
          'fluxo-caixa': 'Cash Flow',
          resultado: 'Result',
          'contas-pagar': 'Payables',
          'coming-soon': 'Coming soon',
        },
        filters: {
          type: 'Diagnosis type',
          'date-from': 'Start date',
          'date-to': 'End date',
          generate: 'Generate Diagnosis',
          generating: 'Generating...',
        },
        generating: {
          title: 'Generating your diagnosis',
          subtitle: 'We are analysing the company data to produce an executive read of the period.',
          collecting: 'Collecting the period data...',
          computing: 'Calculating variations and indicators...',
          analyzing: 'Identifying alerts and opportunities...',
          writing: 'Writing the executive diagnosis...',
        },
        quota: {
          cooldown: 'Wait {0}s to generate a new diagnosis.',
          daily_limit: 'You have reached the limit of {0} diagnoses per day. Contact support to raise it.',
          monthly_limit: 'You have reached the limit of {0} diagnoses this month.',
          concurrent: 'A diagnosis is already being generated.',
          token_cap: 'Your company has reached its AI usage limit this month.',
          cost_cap: 'Your company has reached its AI usage limit this month.',
          remaining: '{0} of {1} diagnoses used today',
          'ai-off': 'Automatic analysis unavailable - showing the calculated reading.',
        },
        result: {
          'cash-grew': 'Your cash grew',
          'cash-shrank': 'Your cash shrank',
          'in-period': 'in the period',
          'cash-will-grow': 'Your cash is expected to grow',
          'cash-will-shrink': 'Your cash is expected to shrink',
          'by-period-end': 'by the end of the period',
          'closing-balance-projected': 'Projected Closing Balance',
          'opening-balance': 'Opening Balance',
          'closing-balance': 'Closing Balance',
          timeline: {
            title: 'Cash timeline',
            subtitle: 'How the balance behaved day by day and the main inflows and outflows of the period.',
            inflow: 'Inflow',
            outflow: 'Outflow',
            endpoints: 'Opening/closing balance',
            start: 'Start',
            end: 'End',
          },
          negative: {
            'title-one': 'Your cash was negative for {0} days',
            'title-many': 'Your cash went negative {0} times ({1} days in total)',
            'body-one': 'Between {0} and {1} the balance was below zero.',
            'body-many': 'The balance went in and out of the red {0} times in the period - a sign of recurring mismatch between payables and receivables.',
            worst: 'The most critical point was {0} on {1}.',
            'days-in-red': 'Days in the red',
            'worst-balance': 'Worst balance',
            'drill-down': 'View Cash Flow for the period',
          },
          suggestions: {
            title: 'How to avoid negative cash: rescheduling suggestions',
            subtitle: 'Small adjustments to payment and receipt dates would keep the balance above zero.',
          },
          'top-movements': {
            title: 'Largest movements of the period',
            subtitle: 'The largest receipts and payments, excluding transfers between your own accounts.',
            'subtitle-projected': 'The largest receipts and payments, including the projected ones from {0} on, excluding transfers between your own accounts.',
            projected: 'Projected',
            inflows: 'Receipts',
            outflows: 'Payments',
            'no-inflows': 'No receipts in the period.',
            'no-outflows': 'No payments in the period.',
          },
          observations: {
            title: 'Other observations',
            badge: 'Beta',
            subtitle: 'Patterns found in the period that do not require rescheduling anything.',
          },
          'no-data': {
            title: 'No cash flow data for this period',
            subtitle: 'Check the selected dates, or whether this client has synced financial data.',
          },
          'projected-from': 'From {0} the balances are projected, not settled.',
          'internal-transfers': '{0} internal transfers between your own accounts were ignored.',
          'generated-at': 'Generated on {0}',
          refresh: 'Refresh',
        },
        error: {
          title: 'Could not generate the diagnosis',
          retry: 'Try again',
          generic: 'Something went wrong. Try again.',
        },
      },
      reports: {
        templates: {
          'field-types': {
            quantity: 'Number',
            brl: 'BRL (R$)',
            usd: 'USD (US$)',
            ratio: 'Percentage (%)',
            short_text: 'Short text',
            long_text: 'Long text',
            dropdown: 'Dropdown',
            attachment: 'Attachment',
          },
          attachment: {
            pick: 'Add files',
            empty: 'No files attached.',
            'upload-error': 'Could not upload the file. Please try again.',
            'download-error': 'Could not open the file. Please try again.',
          },
          builder: {
            'field-label': 'Field label',
            'field-type': 'Type',
            'field-required': 'Required',
            'field-options': 'Options',
            'field-options-hint': 'Press Enter to add each option',
            'field-description': 'Description (optional)',
            'field-description-hint': 'Shown as a tooltip to whoever fills in the report',
            'add-field': 'Add field',
          },
          renderer: {
            empty: 'This template has no fields yet.',
            required: 'Required field',
          },
          visibility: {
            public: 'Public',
            restricted: 'Portfolio-restricted',
          },
          list: {
            title: 'Report templates',
            back: 'Back',
            create: 'New template',
            'delete-title': 'Delete template?',
            'delete-text': 'The template "{0}" will no longer be available for new reports.',
            'delete-hint': 'Reports already created from it, published or draft, stay as they are and keep showing the template name.',
            'delete-confirm': 'Delete',
            columns: {
              name: 'Name',
              description: 'Description',
              fields: 'Fields',
              visibility: 'Visibility',
              actions: 'Actions',
            },
          },
          form: {
            'title-create': 'New template',
            'title-edit': 'Edit template',
            name: 'Template name',
            description: 'Description',
            'public-label': 'Make template public',
            'public-on': 'Public — available to all clients.',
            'public-off': 'Restricted — only appears for the clients in your portfolio.',
            'fields-title': 'Fields',
            cancel: 'Cancel',
            save: 'Save',
            reminders: {
              title: 'Deadline reminders',
              hint: 'Remind the companies in your portfolio to fill this report before it is due. Companies that already published it for the period are not reminded.',
              enabled: 'Send deadline reminders',
              recurring: 'Recurring report',
              'recurring-on': 'Due every period, on the day below of the month after the period ends.',
              'recurring-off': 'Due once, on a specific date.',
              recurrence: 'Frequency',
              monthly: 'Monthly',
              quarterly: 'Quarterly',
              yearly: 'Yearly',
              'due-day': 'Due day',
              'due-day-hint': 'Day of the month after the period closes (1–31).',
              'due-date': 'Due date',
              offsets: 'When to remind',
              'offsets-hint': 'Pick one or more.',
              'offset-before': '{0} days before',
              'offset-before-one': '1 day before',
              'offset-today': 'On the due date',
              'offset-after': '{0} days after',
              'offset-after-one': '1 day after',
              channels: 'Send through',
              'channel-in-app': 'Cockpit notification',
              'channel-email': 'E-mail',
              required: 'Required',
              'due-day-range': 'Use a day between 1 and 31',
              'pick-one': 'Pick at least one',
            },
          },
          published: {
            title: 'Published reports',
            back: 'Back',
            close: 'Close',
            tooltip: 'View published reports',
            export: 'Export to Excel',
            'export-error': 'Could not export the report. Please try again.',
            columns: {
              template: 'Template',
              name: 'Name',
              'reference-date': 'Reference date',
              'published-at': 'Published at',
              actions: 'Actions',
            },
          },
          client: {
            'tab-title': 'Templates',
            'available-title': 'Available templates',
            'my-reports-title': 'My reports',
            fill: 'Fill in',
            status: {
              draft: 'Draft',
              published: 'Published',
            },
            columns: {
              name: 'Name',
              description: 'Description',
              fields: 'Fields',
              template: 'Template',
              'reference-date': 'Reference date',
              status: 'Status',
              actions: 'Actions',
            },
          },
          fill: {
            title: 'Fill in report',
            name: 'Report title',
            'reference-date': 'Reference date',
            cancel: 'Cancel',
            'save-draft': 'Save draft',
            publish: 'Publish',
            'publish-title': 'Publish report',
            'publish-hint': 'Choose the organizations that will be able to see this report.',
            'no-organizations': 'No organizations available to publish to.',
            organizations: 'Organizations',
            'confirm-publish': 'Publish',
            'required-error': 'Fill in all required fields before publishing.',
            'published-locked': 'This report is published and can no longer be edited. You can still send it to other organizations.',
            'template-unavailable': 'This template is no longer available for new reports.',
            share: 'Send to other organizations',
            'share-title': 'Send to other organizations',
            'share-hint': 'Organizations that already received this report stay selected. Only the ones you add now will be notified.',
            'confirm-share': 'Send',
          },
        },
      },
      open: 'open',
      close: 'close',
      noDataText: 'No record found',
      enabled: 'Enabled',
      disabled: 'Disabled',
      details: 'Details',
      'go-back-btn': {
        text: 'Go Back',
      },
      layout: {
        menu: {
          'create-client': {
            title: '+ Create New Client',
          },
          // TECH-2671 — suffix on a personal account in the account selector.
          'my-portfolio': 'My Portfolio',
          diagnostico: {
            title: 'Diagnosis',
          },
          dashboard: {
            title: 'Home',
          },
          myCompanies: {
            title: 'Companies',
          },
          cockpit: {
            title: 'Cockpit',
          },
          configuration: {
            title: 'Configuration',
          },
          dataroom: {
            title: 'Dataroom',
          },
          'company-area': {
            title: 'Company Area',
            description: 'Configure dashboards and company operation areas',
          },
          portfolio: {
            title: 'Portfolio',
          },
          profile: {
            title: 'Profile',
          },
          contact: {
            title: 'Contact',
          },
          logout: {
            title: 'Logout',
          },
        },
      },
      'access-denied': {
        title: 'Access Denied',
        message: "You don't have permission to access this client. Please contact your account manager.",
        logout: 'Logout',
      },
      portfolio: {
        title: 'Portfolio',
        'search-placeholder': 'Search by company name or Tax Id...',
        'export-button': 'Export responses',
        'company-users': {
          button: 'Users',
          title: 'Company users',
          subtitle: 'People outside your company who can follow a portfolio company. Invited users see only the companies they were invited to.',
          'company-select': 'Company',
          'select-hint': 'Select a company to see and invite its users.',
          'personal-account-blocked': 'A personal account cannot manage company users: it follows the portfolio of other companies and cannot invite anyone.',
          'search-placeholder': 'Search by name or e-mail...',
          'invite-button': 'Invite user',
          empty: 'No users yet',
          cancel: 'Cancel',
          error: 'Something went wrong. Please try again.',
          columns: {
            name: 'Name',
            email: 'E-mail',
          },
          remove: {
            action: 'Remove access',
            title: "Remove this user's access to the company?",
            confirm: 'Remove',
            success: 'Access removed',
          },
          'pending-invites': {
            title: 'Pending invites',
            cancel: {
              action: 'Cancel invite',
              title: 'Cancel this invite?',
              confirm: 'Cancel invite',
            },
          },
          invite: {
            title: 'Invite user',
            name: 'Name',
            email: 'E-mail',
            hint: 'The invite is sent by e-mail. After signing in to the Cockpit, the user sees only this company in the Portfolio.',
            send: 'Send invite',
            required: 'Required',
            'invalid-email': 'Invalid e-mail',
            success: 'Invite sent',
            error: 'Could not send the invite',
          },
        },
        'company-invite': {
          title: 'Portfolio invite',
          message: 'You were invited to follow this company in the Cockpit Portfolio:',
          accept: 'Accept',
          decline: 'Decline',
        },
        answers: {
          title: 'Responses',
          'reports-button': 'Reports',
          'templates-button': 'Templates',
          'export-error': 'Could not export the responses. Please try again.',
          'no-selection-warning': 'Select the reports you want to export',
          'selected-count': '{count} report(s) selected',
          'select-all': 'Select all {count}',
          'clear-selection': 'Clear selection',
          columns: {
            company: 'Company',
            document: 'Tax Id',
            template: 'Template',
            'reference-date': 'Reference Date',
            'published-at': 'Published At',
            actions: 'Actions',
          },
          filters: {
            template: 'Template',
            'reference-date': 'Reference Date',
            'publication-date': 'Publication Date',
            from: 'From',
            to: 'To',
          },
        },
        table: {
          column: {
            name: 'Name',
            segment: 'Segment',
            modules: 'Modules',
            actions: 'Actions',
          },
          modules: {
            reports: 'Reports',
            dataroom: 'Dataroom',
            accounting: 'Accounting',
            captable: 'Captable',
            financial: 'Financial',
            'company-area': 'Company Area',
          },
          'dataroom-tooltip': 'Go to dataroom',
        },
      },
      'my-companies': {
        home: {
          title: {
            text: 'Companies',
          },
          'card-header': {
            'search-input': {
              placeholder: 'Search by Name or Tax Id...',
            },
            'filter-companies-btn': {
              text: 'Filter Companies',
            },
            'generate-cockpit-btn': {
              text: 'Generate Cockpit',
            },
          },
          table: {
            column: {
              name: {
                text: 'Name',
              },
              'tax-id': {
                text: 'Tax ID',
              },
              segment: {
                text: 'Segment',
              },
              group: {
                text: 'Group',
              },
              organization: {
                text: 'Organization',
              },
              'last-alerts': {
                text: 'Last alerts',
              },
              'updated-at': {
                text: 'Updated at',
              },
              actions: {
                text: 'Actions',
              },
            },
          },
        },
      },
      'create-client': {
        title: {
          text: 'Create a new Client',
        },
        'invite-dialog': {
          title: 'You have received an invite!',
          accept: 'Accept',
          decline: 'Decline',
        },
        form: {
          name: {
            label: 'Name',
            placeholder: 'Name',
          },
          document: {
            label: 'Document',
            placeholder: 'Document',
            rules: {
              required: 'CNPJ is required',
              complete: 'CNPJ must be complete',
              invalid: 'CNPJ format invalid',
            },
          },
          segment: {
            label: 'Segment',
          },
          save: {
            text: 'Save',
          },
          cancel: {
            text: 'Cancel',
          },
        },
      },
      cockpit: {
        'privacy-policy': {
          title: 'Cockpit Privacy Policy',
          label: 'I have read and accept the',
          link: 'Cockpit Privacy Policy',
          button: 'Accept',
          update: {
            title: 'Cockpit Privacy Policy Update',
            text: 'We updated the privacy policy. Please review it and accept it to continue using the Cockpit.',
            link: 'Cockpit Privacy Policy',
            button: 'Accept',
          },
        },
        tabs: {
          summary: {
            title: 'Summary',
          },
          analysis: {
            title: 'Analysis',
            cards: {
              'coming-soon': {
                title: 'Coming Soon!',
                description: "Be the first to know when it's available",
              },
              accounting: {
                title: 'Accounting',
                description: 'Analyze the Balance Sheet, P&L and other financial statements of the company',
              },
              financial: {
                title: 'Financial',
                description: 'Analyze Cash Flow, Financial P&L, Burn Rate, Runway and other financial metrics',
              },
              sales: {
                title: 'Comercial',
                description:
                  "Check out the company's sales metrics. Pipeline, Conversion rate, Churn Rate and other metrics",
              },
              kpis: {
                title: 'Additional Data',
                description: 'Add and monitor supplementary data from your company',
              },
              reports: {
                title: 'Reports',
                description: "Create and manage your company's reports",
              },
              operations: {
                title: 'Operations',
                description: "Track your company's operational metrics",
              },
              funding: {
                title: 'Fundraising & IR',
                description: 'Manage fundraising and investor and lender relationships. Create a real-time dataroom.',
              },
            },
          },
          alerts: {
            title: 'Alerts',
          },
        },
        financial: {
          title: {
            text: 'Financial Analysis',
          },
          tabs: {
            cashflow: {
              title: 'Cash Flow',
            },
            'income-statement': {
              title: 'Cash Flow Statement',
            },
            'consolidated-cash-balance': {
              title: 'Cash Balance',
            },
            'other-indicators': {
              title: 'Other Indicators',
            },
            'expected-realized': {
              title: 'Budget vs Actual',
            },
            summary: {
              title: 'Summary',
            },
            receipts: {
              title: 'Receipts',
            },
            payments: {
              title: 'Payments',
            },
            budget: {
              title: 'Budget',
            },
            scenarios: {
              title: 'Scenarios',
            },
            orcamento: {
              title: 'Budget Management',
              'add-btn': 'Add',
              'upload-btn': 'Upload Spreadsheet',
              'download-template': 'Download Template',
              indicator: 'Indicator',
              'cost-center': 'Cost Center',
              scenario: 'Scenario',
              'date-from': 'Start Date',
              'date-to': 'End Date',
              name: 'Observation',
              identifier: 'Identifier',
              'reference-date': 'Reference Date',
              value: 'Value',
              actions: 'Actions',
              'dialog-new': 'New Budget',
              'dialog-edit': 'Edit Budget',
              'dialog-delete-title': 'Remove Budget',
              'dialog-delete-message': 'Are you sure you want to remove this budget?',
              'upload-title': 'Spreadsheet Upload',
              'upload-select': 'Select XLSX file',
              'upload-confirm': 'Upload',
              'preview-title': 'Spreadsheet Preview',
              'preview-confirm': 'Confirm',
              'template-title': 'Download Template',
              'template-download': 'Download',
              cancel: 'Cancel',
              save: 'Save',
              delete: 'Remove',
              'save-success': 'Budget saved successfully!',
              'delete-success': 'Budget removed successfully!',
              'upload-success': 'Spreadsheet uploaded successfully!',
              'upload-error': 'Error uploading spreadsheet',
              'template-success': 'Template downloaded successfully!',
              'template-error': 'Error downloading template',
            },
            'burn-rate': {
              title: 'Burn Rate',
            },
            runaway: {
              title: 'Runaway',
            },
            'cash-flow-projection': {
              title: 'Daily Cash Flow',
              'export-excel': {
                tooltip: 'Export to Excel',
                error: 'Could not generate the spreadsheet. Please try again.',
                empty: 'Nothing to export for the selected filters.',
                truncated: 'Export capped at 20,000 rows. Narrow the period to get the full range.',
                columns: {
                  date: 'Date',
                  type: 'Type',
                  'type-in': 'Inflow',
                  'type-out': 'Outflow',
                  customer: 'Customer/Supplier',
                  category: 'Category',
                  'cost-center': 'Cost center',
                  document: 'Document',
                  description: 'Description',
                  'bank-account': 'Bank account',
                  'issue-date': 'Issue date',
                  'due-date': 'Due date',
                  'payment-date': 'Payment date',
                  amount: 'Amount',
                  'original-amount': 'Original amount',
                  'paid-amount': 'Paid amount',
                  'remaining-amount': 'Outstanding',
                  balance: 'Balance',
                  'external-id': 'Omie ID',
                  origin: 'Origin',
                  'origin-forecast': 'Omie forecast',
                },
              },
              'initial-balance': 'Initial Balance',
              'final-balance': 'Final Balance',
              'loading-more': 'Loading more transactions...',
              projected: 'Projected',
              realized: 'Realized',
              'movement-details': 'Movement Details',
              'forecast-badge': 'Forecast',
              'forecast-tooltip': 'Forecast entry in Omie — not a receivable or payable yet',
              origin: 'Origin',
              'origin-forecast': 'Omie forecast',
              'forecast-origins': {
                purchase_order: 'Purchase order (forecast)',
                purchase_invoice: 'Incoming invoice (forecast)',
                monthly_contract: 'Monthly contract (forecast)',
                quote: 'Quote (forecast)',
              },
              client: 'Client',
              category: 'Category',
              account: 'Account',
              value: 'Value',
              'document-number': 'Document Number',
              'issue-date': 'Issue Date',
              'due-date': 'Due Date',
              'expected-payment-date': 'Expected Payment Date',
              'payment-date': 'Payment Date',
              'original-amount': 'Original Amount',
              'paid-amount': 'Paid Amount',
              'remaining-amount': 'Remaining Amount',
              'cost-center': 'Cost Center',
              description: 'Description',
              status: 'Status',
              pagination: {
                'page-info': 'Page {current} of {total}',
              },
              'empty-states': {
                'no-realized-transactions': 'No realized transactions found',
                'no-projected-transactions': 'No projected transactions found',
              },
              filters: {
                account: {
                  label: 'Account',
                  'search-placeholder': 'Search account...',
                  'selected-count': '{count} accounts selected',
                  'no-linked-account': 'No linked account',
                },
                client: {
                  label: 'Client',
                  'search-placeholder': 'Search client...',
                  'selected-count': '{count} clients selected',
                  'no-results': 'No results found',
                  'search-hint': 'Type to search for clients...',
                },
                'date-from': 'Date From',
                'date-to': 'Date To',
                buttons: {
                  cancel: 'Cancel',
                  confirm: 'Confirm',
                  apply: 'Apply',
                },
                validation: {
                  'invalid-date': 'Enter a valid date',
                  'end-before-start': 'End date must not be earlier than the start date',
                },
              },
            },
            'cash-flow-table': {
              title: 'Cash Flow Table',
              loading: 'Loading balance data required to display the cash flow statement. Please wait...',
              actions: {
                'from-to': {
                  text: 'From-To',
                },
                'x-ray': {
                  text: 'X-Ray',
                  tabs: {
                    table: {
                      title: 'Table',
                    },
                    dashboard: {
                      title: 'Dashboard',
                    },
                  },
                },
              },
              'cash-flow-tables': {
                title: {
                  operations: 'Cash Flow from Operations',
                  financing: 'Cash Flow from Financing',
                  investment: 'Cash Flow from Investment',
                  total: 'Total Cash Flow',
                  resume: 'Summary',
                },
                rows: {
                  'cash-in': 'Cash in',
                  'cash-out': 'Cash out',
                  result: 'Result',
                  'total-cash-flow': 'Total Cash Flow',
                  'beginning-of-period': 'Beginning of Period',
                  'end-of-period': 'End of Period',
                },
              },
              'from-to-table': {
                titles: {
                  'operations-cash-in': 'Operations Cash In',
                  'operations-cash-out': 'Operations Cash Out',
                  'operations-result': 'Operations Result',
                  'financing-cash-in': 'Financing Cash In',
                  'financing-cash-out': 'Financing Cash In',
                  'financing-result': 'Financing Result',
                  'investment-cash-int': 'Investment Cash In',
                  'investment-cash-out': 'Investment Cash Out',
                  'investment-result': 'Investment Result',
                  'total-cash-flow': 'Total Cash Flow',
                },
                cols: {
                  'category-identifier': 'Account Number',
                  account: 'Account',
                  'client-account': 'Client Account',
                  type: 'Type',
                  value: 'Value',
                },
                rows: {
                  total: 'Total',
                },
              },
            },
          },
        },
        indicators: {
          title: {
            text: 'Performance Data',
          },
          tabs: {
            evap: {
              title: 'EVAP',
            },
            kpi: {
              title: 'KPI X-Ray',
              header: {
                'create-btn': {
                  text: 'Enter Manually',
                },
                'upload-spreadsheet-btn': {
                  text: 'Upload Spreadsheet',
                },
                'spreadsheet-btn': {
                  text: 'Download Spreadsheet Template',
                  'start-month': {
                    text: 'Start Month',
                  },
                  'end-month': {
                    text: 'End Month',
                  },
                  select: {
                    'select-all': 'Select All',
                    'select-indicators': 'Select Data',
                    error: 'Select at least one indicator',
                  },
                  preview: {
                    title: 'Preview of the KPI Table',
                    tipo: 'Type',
                  },
                  cancel: {
                    text: 'Cancel',
                  },
                  confirm: {
                    text: 'Confirm',
                  },
                  download: {
                    text: 'Download',
                  },
                  upload: {
                    text: 'Upload',
                    success: 'Additional Data updated',
                    successExpected: 'Expected spreadsheet uploaded successfully',
                    error: 'Invalid Spreadsheet',
                    invalid: 'Invalid numeric values found',
                    row: 'Row',
                    column: 'Column',
                  },
                },
              },
              actions: {
                'x-ray': {
                  text: 'X-Ray',
                },
                edit: {
                  text: 'Edit',
                },
                delete: {
                  text: 'Delete',
                },
              },
              'edit-form': {
                title: {
                  text: 'Update Indicator for reference month',
                },
              },
              form: {
                title: {
                  text: 'Manual Data Entry',
                },
                'redirect-indicator-configuration-btn': {
                  text: 'Register New Data',
                },
                fields: {
                  'reference-date': {
                    placeholder: 'Referente Month',
                  },
                  indicator: {
                    placeholder: 'Data',
                  },
                },
                save: {
                  text: 'Save',
                },
                cancel: {
                  text: 'Cancel',
                },
              },
              'no-indicators-warning': {
                text: 'No Indicators found',
                'insert-first': 'No Indicators found, create the first one here',
              },
              loading: {
                text: 'Loading indicators…',
                'updating-text': 'Updating…',
              },
            },
            'kpi-table': {
              title: 'Inserted Data',
            },
            'daily-cash-flow': {
              title: 'Daily Cash Flow',
              'date-from': 'Date From',
              'date-to': 'Date To',
              'no-data': 'No data available for the selected period.',
              company: 'Company',
              'share-value': 'Share',
              'total-value': '100%',
              ungrouped: 'Ungrouped',
              'unnamed-account': 'No bank account',
            },
          },
        },
        accounting: {
          title: {
            text: 'Accounting Analysis',
          },
        },
        reports: {
          home: {
            title: 'My Reports',
            'create-report-btn': {
              text: 'Create Report',
            },
            'create-dialog': {
              title: 'Create Report',
              'from-scratch': {
                title: 'Create from Scratch',
                description: 'Build a report from a blank form.',
              },
              'from-template': {
                title: 'Use a Template',
                description: 'Start from one of the available templates.',
              },
              'use-template': 'Use Template',
              'coming-soon': 'Coming soon',
              'no-templates': 'No templates available.',
              fields: '{count} fields',
              owner: 'Template by {name}',
              back: 'Back',
              cancel: 'Cancel',
            },
            tabs: {
              drafts: {
                title: 'Reports',
              },
              'real-estate': {
                title: 'Real Estate Reports',
              },
            },
            'draft-list': {
              columns: {
                name: 'Name',
                'reference-date': 'Reference Date',
                'created-by': 'Created By',
                visibility: 'Visibility',
                actions: 'Actions',
              },
              visibility: {
                public: 'Public',
                private: 'Private',
              },
            },
            pending: {
              title: 'Pending reports',
              subtitle: 'Overdue or due in the next {days} days',
              period: 'For {period}',
              'due-on': 'due on {date}',
              'due-in': 'Due in {days} days',
              'due-in-one': 'Due in 1 day',
              'due-today': 'Due today',
              overdue: '{days} days overdue',
              'overdue-one': '1 day overdue',
              quarter: 'Q{quarter} {year}',
              fill: 'Fill report',
              'continue-draft': 'Continue draft',
              ignore: 'Ignore reminder',
              'ignore-hint': 'Ignore the reminder for this period',
              'ignore-title': 'Ignore this reminder?',
              'ignore-confirm': 'You will stop receiving reminders for "{report}" for {period}, here and by e-mail. Other periods keep reminding you as usual.',
              'ignore-confirm-action': 'Ignore reminder',
              'ignore-cancel': 'Cancel',
            },
          },
          'reports-form': {
            title: {
              text: 'Create Report',
            },
            fields: {
              name: {
                label: 'Name',
              },
              observations: {
                label: 'Comments',
              },
              'reference-date': {
                label: 'Reference Date',
              },
              public: {
                label: 'Public',
              },
            },
            'create-btn': {
              text: 'Create Report',
            },
            'delete-draft-btn': {
              text: 'Delete Draft',
            },
            modules: {
              indicators: {
                title: 'Indicators',
                table: {
                  'indicator-tooltip': {
                    text: 'Click to edit the indicator value in the report',
                  },
                  'indicator-form-edit': {
                    title: 'Edit Indicator for Report',
                  },
                  'start-reference-date': {
                    placeholder: 'Start Reference Date',
                  },
                  'end-reference-date': {
                    placeholder: 'End Reference Date',
                  },
                },
              },
              financial: {
                title: 'Financial',
                'cash-flow-table': {
                  title: 'Financial',
                  tooltip: {
                    text: 'Click to edit the indicator value in the report',
                  },
                  'start-reference-date': {
                    placeholder: 'Start Reference Date',
                  },
                  'end-reference-date': {
                    placeholder: 'End Reference Date',
                  },
                  'edit-form': {
                    title: 'Edit Cash Flow',
                    'reference-date': {
                      placeholder: 'Reference Date',
                    },
                    name: {
                      text: 'Account',
                    },
                    value: {
                      text: 'Value',
                    },
                    'save-btn': {
                      text: 'Save',
                    },
                    'cancel-btn': {
                      text: 'Cancel',
                    },
                  },
                },
              },
              attachments: {
                title: 'Attachments',
                'upload-btn': {
                  text: 'Upload',
                },
                'upload-dialog': {
                  title: 'Upload Attachments',
                  form: {
                    name: {
                      text: 'Name',
                    },
                    description: {
                      text: 'Description',
                    },
                    observations: {
                      text: 'Observations',
                    },
                    save: {
                      text: 'Save',
                    },
                    cancel: {
                      text: 'Cancel',
                    },
                    filename: {
                      text: 'Filename',
                    },
                  },
                },
                table: {
                  column: {
                    name: {
                      text: 'Name',
                    },
                    filename: {
                      text: 'Filename',
                    },
                    description: {
                      text: 'Description',
                    },
                    actions: {
                      text: 'Actions',
                    },
                  },
                },
              },
            },
          },
        },
        sales: {
          title: {
            text: 'Sales Analysis',
          },
          tabs: {
            summary: {
              title: 'Summary',
            },
            ticket: {
              title: 'Ticket Breakdown',
            },
            product: {
              title: 'Portfólio de Produtos',
            },
            team: {
              title: 'Sales Team',
            },
          },
        },
        operations: {
          title: {
            text: 'Operations Analysis',
          },
          tabs: {
            summary: {
              title: 'Summary',
            },
            inventory: {
              title: 'Inventory',
            },
          },
        },
        funding: {
          title: {
            text: 'Fundraising & IR',
          },
          tabs: {
            captable: {
              table: {
                title: 'Cap Table',
                total: {
                  title: 'Total of Shares',
                },
              },
              title: 'Cap Table',
              header: {
                'create-btn': {
                  text: 'Create',
                },
                'no-data': {
                  text: 'No data',
                },
                period: {
                  label: 'Period',
                  'last-12': 'Last 12 months',
                  'last-24': 'Last 24 months',
                  'last-36': 'Last 36 months',
                  'last-60': 'Last 5 years',
                  all: 'All data',
                },
              },
              actions: {
                delete: {
                  text: 'Delete',
                },
                edit: {
                  text: 'Edit',
                },
              },
              messages: {
                'save-error': 'Could not save the cap table entry.',
              },
              form: {
                'create-title': {
                  text: 'Add Shareholder',
                },
                'edit-title': {
                  text: 'Edit Cap Table',
                },
                fields: {
                  'reference-date': {
                    placeholder: 'Reference Date',
                    'required-message': 'Reference Date is required',
                  },
                  share: {
                    placeholder: 'Number of Units or Shares',
                    'required-message': 'Share is required',
                    min: 'Share must be greater than 0',
                  },
                  name: {
                    placeholder: 'Name',
                    'required-message': 'Name is required',
                  },
                },
                save: {
                  text: 'Save',
                },
                cancel: {
                  text: 'Cancel',
                },
                delete: {
                  title: 'Confirm Delete?',
                  cancel: 'CANCEL',
                  confirm: 'DELETE',
                },
              },
            },
          },
        },
      },
      'company-area': {
        title: 'Company Area',
        description: 'Configure dashboards and company operation areas',
        'enable-company-area': 'Enable Company Area',
        messages: {
          'config-saved': 'Configuration saved successfully',
          'config-error': 'Error saving configuration',
        },
        tabs: {
          summary: {
            title: 'Operations',
          },
          client: {
            title: 'Sales',
          },
          analysis: {
            title: 'Analysis',
          },
        },
      },
      auth: {
        'sign-in': {
          needsEmailVerification: {
            text: 'You need to verify your email',
          },
          email: {
            label: 'Email',
          },
          password: {
            label: 'Password',
          },
          'login-btn': {
            text: 'Login',
          },
          'forgot-password-btn': {
            text: 'Forgot Password',
          },
          'first-access-btn': {
            text: 'First Access',
          },
          'remember-me-checkbox': {
            text: 'Remember Me',
          },
          'wrong-credentials': {
            text: 'Invalid email or password',
          },
        },
        'sign-up': {
          needsEmailVerification: {
            text: 'Please check your mailbox and follow the verification link to verify your email.',
          },
          'first-name': {
            label: 'First Name',
          },
          'last-name': {
            label: 'Last Name',
          },
          email: {
            label: 'Email',
          },
          password: {
            label: 'Password',
          },
          'confirm-password': {
            label: 'Confirm Password',
          },
          'sign-up-btn': {
            text: 'Sign Up',
          },
          'go-back-btn': {
            text: 'Go Back',
          },
          'password-mismatch': {
            text: 'Password does not match',
          },
          'email-already-registered': {
            text: 'Email is already registered',
            prefix: 'Login',
            link: 'here',
          },
        },
        'change-password': {
          message: {
            title: 'Reset Password',
            text: 'Choose your new password',
          },
          password: {
            label: 'Password',
          },
          confirmPassword: {
            label: 'Confirm Password',
          },
          'confirm-btn': {
            text: 'Confirm',
          },
        },
        'forgot-password': {
          message: {
            title: 'Forgot your password?',
            text: 'Fill your email and we will send a link to reset your password',
          },
          email: {
            label: 'Email',
          },
          'email-sent-alert': {
            text: 'If this email is registered, a link to set your password has been sent',
          },
          'send-link-btn': {
            text: 'Send Link',
          },
          'sign-in-btn': {
            text: 'Sign In',
          },
          'password-mismatch-alert': {
            text: 'Password does not match',
          },
        },
        'first-access': {
          message: {
            title: 'First Access?',
            text: 'Fill your email and we will send a link to define your password',
          },
          email: {
            label: 'Email',
          },
          'email-sent-alert': {
            text: 'If this email is registered, a link to set your password has been sent',
          },
          'send-link-btn': {
            text: 'Send Link',
          },
          'sign-in-btn': {
            text: 'Sign In',
          },
          'password-mismatch-alert': {
            text: 'Password does not match',
          },
        },
      },
      investorInvite: {
        title: 'Invitation from {0}',
        description: 'Accept this invitation to follow {0}\'s data as an investor.',
        accept: 'Accept invitation',
        needsLogin: {
          text: 'Sign in or create an account to accept this invitation.',
          signIn: 'Sign in',
          signUp: 'Create account',
        },
        needsClient: {
          text: 'You need your own company registered in the platform before accepting an investor invitation.',
          createClient: 'Create my company',
        },
        selectClient: {
          label: 'Accept as',
        },
        grantee: '{0} will follow {1} in its Portfolio.',
        personal: {
          description: 'Accept this invitation to follow {0} in Portfolio. We will create your own personal account — no company registration needed.',
          nameLabel: 'Your name',
          nameHint: 'This is the name of your personal account.',
          reuseAccount: 'This invitation will be added to the personal account you already have.',
          modulesNotice: 'The company releases the Dataroom after you accept, so some areas may still be unavailable at first.',
        },
        existingCompany: {
          text: '{0} is already in your portfolio.',
          link: 'Link to the existing record',
          createNew: 'Create a new record',
        },
        errors: {
          not_found: 'This invitation link is invalid.',
          expired: 'This invitation has expired.',
          revoked: 'This invitation was revoked.',
          already_accepted: 'This invitation was already accepted.',
          refused: 'You declined this invitation.',
          declineFailed: 'Failed to decline the invitation. Please try again.',
          wrong_email: 'This invitation was sent to another e-mail. Sign in with the invited e-mail to accept it.',
          acceptFailed: 'Failed to accept the invitation. Please try again.',
        },
        switchAccount: 'Sign in with another account',
        decline: 'Decline',
      },
      configuration: {
        title: 'Configuration',
        tabs: {
          modules: {
            title: 'Modules',
            cards: {
              accounting: {
                title: 'Accounting',
                description: 'Analyze the Balance Sheet, P&L and other financial statements of the company',
              },
              financial: {
                title: 'Financial',
                description: 'Analyze Cash Flow, Financial P&L, Burn Rate, Runway and other financial metrics',
              },
              sales: {
                title: 'Comercial',
                description:
                  "Check out the company's sales metrics. Pipeline, Conversion rate, Churn Rate and other metrics",
              },
              indicators: {
                title: 'Performance Data',
                description: "Metrics and Additional Data of your company's operational activities",
              },
              dataroom: {
                title: 'Dataroom',
                description: 'Centralize and share important company documents securely',
              },
              webhooks: {
                title: 'Webhooks',
                description: 'Manage webhook subscriptions to receive real-time notifications',
              },
              'api-keys': {
                title: 'API Keys',
                description: 'Create and manage API keys for programmatic access to your data',
              },
            },
          },
          roles: {
            title: 'Roles',
            list: {
              title: 'List',
            },
            filter: {
              title: 'Filter',
            },
            create: {
              title: 'Create New Role',
            },
            edit: {
              title: 'Edit Role',
            },
            delete: {
              title: 'Delete Role',
            },
            name: {
              title: 'Name',
            },
            actions: {
              title: 'Actions',
            },
            description: {
              title: 'Description',
            },
            permissions: {
              title: 'Permissions',
              management: 'Manage Permissions',
              selectAllRead: 'Select Read Permissions',
              unselectAllRead: 'Unselect Read Permissions',
              selectAllEdit: 'Select Edit Permissions',
              unselectAllEdit: 'Unselect Edit Permissions',
            },
            role: {
              title: 'Role',
            },
            save: {
              title: 'Save',
              success: 'Roles updated successfully',
            },
            cancel: {
              title: 'Cancel',
            },
            delete_confirmation: {
              title: 'Are you sure you want to delete this role?',
            },
          },
          alerts: {
            title: 'Alerts',
          },
          'company-area': {
            title: 'Company Area',
            description: 'Configure dashboards for the company area',
            'enable-company-area': 'Enable Company Area',
            messages: {
              'config-saved': 'Configuration saved successfully',
              'config-error': 'Error saving configuration',
            },
            'dashboard-type': {
              title: 'Dashboard Type',
              label: 'Dashboard Type',
              select: 'Select dashboard type',
              'selected-type': 'Selected type: {type}',
              'no-type-selected': 'None',
            },
            buttons: {
              edit: 'Edit',
              cancel: 'Cancel',
              confirm: 'Confirm',
            },
            'operation-area': {
              title: 'Operation Area',
              select: 'Select operation area',
              'selected-area': 'Selected area: {area}',
              'no-area-selected': 'None',
              options: {
                operacao: 'Operation',
                comercial: 'Commercial',
                analise: 'Analysis',
              },
              description: {
                operacao: 'Configure dashboards for the operation area',
                comercial: 'Configure dashboards for the commercial area',
                analise: 'Configure dashboards for the analysis area',
              },
            },
            'select-type': {
              title: 'Select a dashboard type',
              description: 'Choose between Metabase, Google Sheets or Public URL to configure company dashboards.',
            },
            dashboards: {
              title: 'Dashboard Configuration',
              'not-configured': 'Not configured',
              display: {
                label: 'Display dashboard',
                yes: 'Yes',
                no: 'No',
              },
              tooltip: {
                'embed-url': 'The URL must contain "/embed" to work correctly',
              },
              messages: {
                'save-success': 'Configuration saved successfully',
                'save-error': 'Error saving configuration',
                'test-success': 'Connection test successful',
                'test-error': 'Connection test failed',
                'remove-success': 'Configuration removed successfully',
                'remove-error': 'Error removing configuration',
                'metabase-success': 'Metabase dashboard configured successfully!',
                'googlesheets-success': 'Google Sheets dashboard configured successfully!',
                'publicurl-success': 'Public URL dashboard configured successfully!',
                'metabase-error': 'Error saving Metabase configuration',
                'googlesheets-error': 'Error saving Google Sheets configuration',
                'publicurl-error': 'Error saving Public URL configuration',
                'validation-error': 'Please fill in all required fields',
                'load-error': 'Error loading existing configurations',
              },
              metabase: {
                title: 'Metabase',
                url: 'Metabase URL',
                urlPlaceholder: 'https://your-metabase.com',
                apiKey: 'API Key',
                apiKeyPlaceholder: 'Enter your Metabase API key',
                dashboardId: 'Dashboard ID',
                dashboardIdPlaceholder: 'Ex: 1',
                save: 'Save',
              },
              googleSheets: {
                title: 'Google Sheets',
                url: 'Google Sheets URL',
                urlPlaceholder: 'https://docs.google.com/spreadsheets/d/...',
                save: 'Save',
              },
              publicUrl: {
                title: 'Public URL',
                url: 'Public URL',
                urlPlaceholder: 'https://example.com/dashboard',
                save: 'Save',
              },
              saved: {
                title: 'Saved Configurations',
                empty: {
                  title: 'No configurations saved yet.',
                  subtitle: 'Add dashboards above to get started.',
                },
                test: 'Test',
                remove: 'Remove',
                types: {
                  metabase: 'Metabase',
                  'google-sheets': 'Google Sheets',
                  'public-url': 'Public URL',
                },
              },
            },
          },
          webhooks: {
            title: 'Webhooks',
            description: 'Manage webhook subscriptions to receive real-time notifications',
            'new-btn': 'New Webhook',
            headers: {
              name: 'Name',
              url: 'URL',
              events: 'Events',
              status: 'Status',
              'created-at': 'Created at',
              actions: 'Actions',
            },
            status: {
              active: 'Active',
              paused: 'Paused',
            },
            actions: {
              test: 'Send test',
              logs: 'Delivery log',
              edit: 'Edit',
              pause: 'Pause',
              resume: 'Resume',
              delete: 'Delete',
            },
            'event-types': {
              captable_updated: 'Cap table updated',
              receivables_updated: 'Receivables updated',
              payables_updated: 'Payables updated',
              accounting_transactions_updated: 'Accounting transactions updated',
              financial_transactions_updated: 'Financial transactions updated',
            },
            form: {
              'create-title': 'New Webhook',
              'edit-title': 'Edit Webhook',
              name: 'Name',
              url: 'URL',
              'event-types': 'Events',
              cancel: 'Cancel',
              save: 'Save',
              rules: {
                'name-required': 'Name is required',
                'url-required': 'URL is required',
                'url-https': 'URL must start with https://',
                'event-types-required': 'Select at least one event',
              },
            },
            secret: {
              title: 'Signing secret',
              warning: 'This is the only time the secret is shown. Copy it now — it cannot be retrieved again.',
              copy: 'Copy',
              copied: 'Secret copied to clipboard',
              done: 'Done',
            },
            'delete-confirmation': {
              title: 'Delete this webhook?',
              text: 'This webhook is active and will stop receiving events immediately. This action can be reversed by creating a new subscription.',
            },
            'delivery-log': {
              title: 'Recent deliveries',
              close: 'Close',
              headers: {
                event: 'Event',
                status: 'Status',
                attempts: 'Attempts',
                'delivered-at': 'Delivered at',
                error: 'Error',
              },
            },
            messages: {
              'load-error': 'Error loading webhooks',
              'create-success': 'Webhook created successfully',
              'create-error': 'Error creating webhook',
              'update-success': 'Webhook updated successfully',
              'update-error': 'Error updating webhook',
              'delete-success': 'Webhook deleted successfully',
              'delete-error': 'Error deleting webhook',
              'test-success': 'Test delivered successfully (status {status})',
              'test-error': 'Test delivery failed: {error}',
            },
          },
          'api-keys': {
            title: 'API Keys',
            description: 'Create and manage API keys for programmatic access to your data',
            'new-btn': 'New API Key',
            tabs: {
              keys: 'Keys',
              'access-requests': 'Access requests',
            },
            headers: {
              name: 'Name',
              prefix: 'Prefix',
              scopes: 'Scopes',
              status: 'Status',
              'created-at': 'Created at',
              actions: 'Actions',
            },
            status: {
              active: 'Active',
              revoked: 'Revoked',
            },
            actions: {
              revoke: 'Revoke',
            },
            scopes: {
              financial: 'Financial',
              accounting: 'Accounting',
              webhooks: 'Webhooks',
              reports: 'Reports',
              captable: 'Cap table',
              complementary: 'Complementary data',
            },
            form: {
              'create-title': 'New API Key',
              name: 'Name',
              scopes: 'Scopes',
              cancel: 'Cancel',
              save: 'Save',
              rules: {
                'name-required': 'Name is required',
                'scopes-required': 'Select at least one scope',
              },
            },
            token: {
              title: 'API key token',
              warning: 'This is the only time the full token is shown. Copy it now — it cannot be retrieved again.',
              copy: 'Copy',
              copied: 'Token copied to clipboard',
              done: 'Done',
            },
            'revoke-confirmation': {
              title: 'Revoke this API key?',
              text: 'This action cannot be undone. Any integration using this key will stop authenticating immediately.',
            },
            messages: {
              'load-error': 'Error loading API keys',
              'create-success': 'API key created successfully',
              'create-error': 'Error creating API key',
              'revoke-success': 'API key revoked successfully',
              'revoke-error': 'Error revoking API key',
            },
            'access-requests': {
              'new-btn': 'Request access',
              outgoing: {
                title: 'Sent requests',
              },
              incoming: {
                title: 'Received requests',
              },
              headers: {
                'target-client': 'Client',
                'requester-client': 'Client',
              },
              status: {
                pending: 'Pending',
                accepted: 'Accepted',
                refused: 'Refused',
                revoked: 'Revoked',
              },
              actions: {
                accept: 'Accept',
                refuse: 'Refuse',
                revoke: 'Revoke access',
              },
              dialog: {
                title: 'Request access to another client\'s data',
                key: 'Key',
                client: 'Client',
                submit: 'Send request',
              },
              'revoke-confirmation': {
                title: 'Revoke this access?',
                text: 'The requesting client will immediately lose access to your data through this key.',
              },
              messages: {
                'create-success': 'Access request sent',
                'create-error': 'Error sending access request',
                'accept-success': 'Access granted',
                'refuse-success': 'Request refused',
                'revoke-success': 'Access revoked',
                'action-error': 'Error processing the request',
              },
            },
          },
          investors: {
            title: 'Investors',
            description: 'Invite an investor to follow your company\'s data in Portfolio',
            'new-btn': 'Invite investor',
            'search-placeholder': 'Search by investor or e-mail...',
            'no-search-results': 'No investor matches the search.',
            tabs: {
              invites: 'Invitations',
              modules: 'Enabled modules',
            },
            modules: {
              dataroom: 'Dataroom',
              reports: 'Reports',
              'reports-hint': 'This investor can only access the reports you published for them.',
            },
            headers: {
              email: 'Email',
              investor: 'Investor',
              kind: 'Account',
              status: 'Status',
              'created-at': 'Created at',
              'expires-at': 'Expires at',
              actions: 'Actions',
            },
            kind: {
              corporate: 'Corporate account',
              personal: 'Personal account',
            },
            status: {
              pending: 'Pending',
              accepted: 'Accepted',
              refused: 'Declined',
              revoked: 'Revoked',
              expired: 'Expired',
            },
            actions: {
              revoke: 'Revoke',
            },
            dialog: {
              title: 'Invite an investor',
              email: 'Investor email',
              'email-required': 'Email is required',
              'email-invalid': 'Enter a valid email',
              cancel: 'Cancel',
              submit: 'Send invitation',
              'kind-label': 'What kind of account is this invitation for?',
              'kind-corporate-hint': 'A company or fund that has (or will create) its own account in the platform, with its own users and modules.',
              'kind-personal-hint': 'A person who will only follow your company in Portfolio. They get an account of their own and cannot invite anyone.',
              'custom-domain-notice': 'The invitation link will use your custom domain:',
              'grant-dataroom': 'Release the Dataroom as soon as they accept',
              'grant-dataroom-hint': 'Without this, you have to come back to this screen after the invitation is accepted to turn the Dataroom on.',
            },
            'revoke-confirmation': {
              title: 'Revoke this invitation?',
              text: 'The investor will lose access to your data on their next request.',
            },
            messages: {
              'create-success': 'Invitation sent',
              'create-error': 'Error sending invitation',
              'revoke-success': 'Invitation revoked',
              'action-error': 'Error processing the invitation',
              'module-update-success': 'Module updated',
            },
          },
          users: {
            title: 'Users',
            tabs: {
              'users-table': {
                header: {
                  'search-input': {
                    placeholder: 'Search by name or email',
                  },
                  'invite-btn': {
                    text: 'Invite User',
                  },
                },
                table: {
                  column: {
                    'display-name': {
                      text: 'Name',
                    },
                    email: {
                      text: 'E-mail',
                    },
                    'client-role': {
                      text: 'Role',
                    },
                    actions: {
                      text: 'Actions',
                    },
                  },
                },
              },
            },
            invite: {
              title: 'Invite User',
              name: {
                placeholder: 'Name',
                'required-message': 'Name is required',
              },
              role: {
                placeholder: 'Role',
                'required-message': 'Role is required',
              },
              email: {
                placeholder: 'E-mail',
                'required-message': 'E-mail is required',
                'invalid-message': 'Invalid e-mail',
                'already-exists-message': 'This email already belongs to an existing user',
              },
              send: {
                text: 'Send',
              },
              cancel: {
                text: 'Cancel',
              },
              success: {
                text: 'User invited successfully',
              },
              error: {
                text: 'Error inviting user',
              },
            },
            'pending-invites': {
              title: 'Pending Invites',
              cancel: {
                title: 'Cancel Invite',
                confirmation: {
                  title: 'Are you sure you want to cancel this invite?',
                },
                cancel: {
                  text: 'Go Back',
                },
              },
              'cancel-success': {
                text: 'Invite canceled successfully',
              },
              'cancel-error': {
                text: 'Error canceling invite',
              },
              table: {
                column: {
                  email: {
                    text: 'E-mail',
                  },
                  actions: {
                    text: 'Actions',
                  },
                },
              },
            },
            remove: {
              title: 'Remove User',
              confirmation: {
                title: 'Remove user?',
              },
              message: 'This operation cannot be reversed.',
            },
            edit_role: {
              title: 'Edit Role',
              select_role: 'Select Role',
            },
            cancel: {
              title: 'Cancel',
            },
            save: {
              title: 'Save',
            },
          },
        },
        dataroom: {
          moduleConfigTitle: 'Visual',
          // Index.vue labels and messages
          logoFull: 'Full Logo',
          insertPhotoUrl: 'Insert photo URL',
          uploadImage: 'Upload image',
          logoShort: 'Short Logo',
          colors: 'Colors',
          primaryColor: 'Primary color',
          secondaryColor: 'Secondary color',
          selectPrimaryColor: 'Select primary color',
          selectSecondaryColor: 'Select secondary color',
          confirm: 'Confirm',
          clientInfo: 'Client Information',
          address: 'Address',
          website: 'Website',
          features: 'Features',
          enableDataroom: 'Enable Dataroom',
          enableCaptable: 'Enable Captable',
          enableFinancialModule: 'Enable Financial Data',
          enableAccountingModule: 'Enable Accounting Data',
          enableIndicatorsModule: 'Enable Complementary Data',
          enableCompanyAreaModule: 'Enable Company Area',
          dashboard: 'Dataroom Home',
          changesSaved: 'Changes saved successfully',
          configurationSaved: 'Configuration saved successfully',
          configurationSaveError: 'Error saving configuration',
          defaultDashboard: 'Cockpit Home',
          dashboardDataroom: 'Custom Home',
          invalidFileType: 'Please select only image files',
          urlGenerationError: 'Error generating image URL',
          embedLinks: 'Embed video or other links',
          companyEmbedUrl: 'Embed Link',
          embedUrlHelp:
            'Paste the link of a public video or post from YouTube, Instagram, LinkedIn, Vimeo, Google Drive or Loom. The share link works — it is converted to the embed format automatically.',
          embedPreview: 'Preview',
          mediaModeLink: 'Link',
          mediaModeUpload: 'Upload video',
          videoUpload: 'Company video',
          videoUploadHelp: 'MP4 (H.264), WebM or MOV up to 500 MB. It plays in the dataroom home with its own player.',
          noVideo: 'No video uploaded',
          selectVideo: 'Select video',
          replaceVideo: 'Replace video',
          removeVideo: 'Remove video',
          validatingVideo: 'Checking video…',
          uploadingVideo: 'Uploading video',
          // Dialogs and common actions
          editDocument: 'Edit Document',
          addDocument: 'Add Document',
          folder: 'Folder',
          documentName: 'Document Name',
          documentLink: 'Document Link',
          uploadFile: 'Upload File',
          uploadedFile: 'File uploaded',
          externalLink: 'External link',
          cancel: 'Cancel',
          save: 'Save',
          edit: 'Edit',
          newFolder: 'New Folder',
          createNewFolder: 'Create New Folder',
          enterNewFolderName: 'Enter new folder name',

          team: 'Team',
          noMembersFound: 'No team members found',
          name: 'Name',
          position: 'Position',
          description: 'Description',
          addMember: 'Add member',

          documents: 'Documents',
          searchDocuments: 'Search documents',
          documentNameHeader: 'Document',
          folderHeader: 'Folder',
          lastModified: 'Last modified',
          noDocuments: 'No documents',
          noFolder: '—',
          addDocumentBtn: 'Add Document',
          allFolders: 'All Folders',
          addDocumentToFolder: 'Add Document to Folder',
          selectFolder: 'Select Folder',

          confirmDelete: 'Confirm delete',
          deleteDocumentConfirm: 'Do you really want to delete the document',
          delete: 'Delete',
          confirmRemoveTeamMember: 'Do you really want to remove the team member?',

          linkedinEmbedUrl: 'Linkedin Embed Link',
          linkedinEmbedUrlHelp:
            'Paste the full embed code provided by the platform. It\'s usually an iframe snippet you copy‑and‑paste as is. Example: <iframe src="https://www.linkedin.com/embed/feed/update/urn:li:activity:123456789"></iframe>',
          linkedinFeed: 'LinkedIn Feed',

          businessPlanPitchDeck: 'Business Plan / Pitch Deck / Roadmap',
          businessPlan: 'Business Plan',
          fileUploaded: 'File uploaded',

          errors: {
            invalidUrl: 'Invalid URL',
            unsupportedVideoUrl: 'Unsupported link. Use a public video or post from YouTube, Instagram, LinkedIn, Vimeo, Google Drive or Loom',
            videoType: 'Unsupported format. Use MP4, WebM or MOV',
            videoSize: 'The video is larger than 500 MB',
            videoUnplayable: "The browser can't play this video. Export it as MP4 (H.264) and try again",
            videoUpload: 'Error uploading the video',
            urlMustStartWith: 'URL must start with http:// or https://',
            pleaseEnterValidUrl: 'Please enter a valid URL',
            invalidInstagramHandle: 'Invalid Instagram handle',
            invalidLinkedinHandle: 'Invalid LinkedIn handle',
            businessPlanRemoved: 'Business plan removed',
            fileOrLinkRequired: 'File or link is required',
            fileUploadError: 'File upload error',
            fileTypeNotAllowed: 'File type not allowed',
            fileTooLarge: 'File too large',
            folderRemoved: 'Failed to remove document',
            missingDocumentFields: 'Fill folder, name and file or link',
          },

          fileTooLarge: 'The file is too large',
          urlMustStartWith: 'URL must start with http:// or https://',
          uploadError: 'File upload error',
          missingDocumentFields: 'Fill folder, name and file or link',
          documentPreparedToSave: 'Document prepared to save',
          memberRemoved: 'Member removed',
          businessPlanRemoved: 'Business plan removed',
          folderRemoved: 'Folder removed',
        },
        financial: {
          'create-config-btn': {
            text: 'Add New Provider',
          },
          form: {
            title: 'Provide your financial provider information',
            'title-filled': 'Provider Information',
            text: 'We will request the data to implement the financial module!',
            'text-filled': 'See or edit the data of the provider registered for the financial module.',
            provider: 'Provider',
            'provider-label': 'Select a provider',
            hide: 'Hide',
            show: 'Show',
            save: 'Save',
            edit: 'Edit',
            cancel: 'Cancel',
            disable: 'Remove Provider',
            'disable-title': 'Remove provider?',
            'disable-confirm':
              'This will remove the current provider for this module. Are you sure you want to continue?',
            'disable-error': 'Failed to remove provider.',
            'disable-success': 'Provider removed successfully.',
            name: 'Provider',
            status: 'Status',
            'no-changes-warning': 'No changes were made to save',
            actions: 'Actions',
            'conta-azul-authenticated': 'Conta Azul is authenticated',
            'conta-azul': {
              step1: 'Follow the steps below',
              step2: 'When creating the partner, use the following data:',
              step3: 'After creating the partner, click the "Save" button to finish the configuration.',
              responsible: 'Responsible Name',
              company: 'Company Name',
              cnpj: 'CNPJ',
              email: 'E-mail',
              phone: 'Phone',
              service: 'Service',
              authorize: 'Authorize with Conta Azul',
              'authorize-text': 'Click to authorize the integration with your Conta Azul account',
              'authorize-success': 'Authorization completed successfully! The code has been filled automatically.',
            },
          },
          'configuration-in-process': {
            title: 'Configuration in progress',
            text: 'Your financial module configuration is in progress. You will be notified when it is complete.',
            button: 'Go Back to Configuration',
          },
          tabs: {
            providers: 'Provider Configuration',
            'chart-accounts': 'Custom Chart of Accounts',
            'expected-chart-accounts': 'Expected Chart of Accounts',
            'account-filter-settings': 'Account filter settings',
          },
          'account-filter-settings': {
            empty: 'No bank accounts found for this client.',
            'toggle-visible': 'Show in cash flow account filter',
            'toggle-default': 'Default account for transactions without a bank account',
            'default-badge': 'Default',
            'default-changed': 'Default account updated — the previous one was automatically unset.',
            save: 'Save',
            discard: 'Discard',
            'update-error': 'Could not update account filter.',
            'update-success': 'Account filter updated successfully.',
            'update-success-on': 'Account enabled in filter successfully.',
            'update-success-off': 'Account disabled in filter successfully.',
          },
          'custom-chart-accounts': {
            disable: 'Disable account',
            enable: 'Enable',
            'revert-disable': 'Undo disable',
            disabled: 'Disabled accounts',
            'disable-title': 'Disable Account',
            'disable-confirm': 'Are you sure you want to disable this account?',
            'disable-success': 'Account disabled successfully',
            'disable-error': 'Error disabling account',
            'no-active-config': 'No active configuration found',
            'disable-button': 'Disable',
            'configurations-to-remove': 'Configurations to remove:',
            'disable-message': 'Disabling this account, the following configurations will be removed:',
            'duplicate-config-error':
              'The destination configuration already exists for this account, delete or edit the existing configuration to proceed',
            'new-configuration': 'New Configuration',
            'new-config-title': 'Create an additional configuration for this account',
            delete: 'Delete',
            'delete-title': 'Delete Configuration',
            'confirm-delete': 'Are you sure you want to delete this configuration?',
            'delete-success': 'Configuration deleted successfully',
            'delete-error': 'Error deleting configuration',
            title: 'Custom Chart of Accounts',
            description: 'View custom accounts configured by category',
            search: 'Search accounts...',
            'account-name': 'Account Name',
            identifier: 'Identifier',
            'category-name': 'Category Name',
            'category-identifier': 'Category Identifier',
            multiplier: 'Multiplier',
            visible: 'Visible',
            configured: 'Configured accounts',
            unconfigured: 'Unconfigured accounts',
            'no-data': 'No custom accounts found for this configuration',
            'no-search-results': 'No results found for your search',
            'try-different-search': 'Try a different search or clear the filter',
            'select-config': 'Select a configuration to view accounts',
            'no-configurations': 'No configurations available',
            category: 'Category',
            edit: 'Edit',
            save: 'Save',
            cancel: 'Cancel',
            approved: 'Validated',
            'save-success': 'Accounts updated successfully!',
            'save-partial': 'Some accounts updated, some had errors.',
            'save-error': 'Error saving changes',
            'no-changes': 'No changes to save',
            'approval-required-tooltip': 'This account needs approval. Confirm approval in edit mode.',
            'confirm-title': 'Confirm Changes',
            'no-changes-message': 'No changes to save',
            'clone-title': 'Clone configuration to children',
            'clone-message': 'The parent configuration will be applied to the following eligible children:',
            'clone-confirm': 'Clone to children',
            'clone-success': 'Configuration cloned to children successfully',
            'clone-error': 'Error cloning configuration',
            'all-children-configured': 'All children are already configured',
            'no-eligible-children-clone': 'No eligible children to clone',
            'cascade-disable-title': 'Disable parent and children',
            'cascade-disable-message': 'Disabling this parent will also disable the following configured children:',
            'cascade-disable-success': 'Parent and children disabled successfully',
            'ignore-cost-center': {
              label: 'Consider cost centers',
              tooltip: 'Off: every cost center of an account uses the same mapping, and new account + cost center combinations from syncs are mapped automatically. Turn it on to map each cost center separately.',
              'enable-title': 'Ignore cost centers',
              'enable-message': 'Every cost center of an account will share one mapping. Unconfigured account + cost center combinations receive their account\'s mapping now, and new ones from future syncs are mapped automatically. Editing the mapping of one cost center updates all cost centers of that account.',
              'enable-note': 'Combinations already configured differently are kept until one of them is edited.',
              'enable-confirm': 'Ignore cost centers',
              'enable-success': 'Cost centers are now ignored in the mapping',
              'disable-title': 'Consider cost centers again',
              'disable-message': 'Current mappings are kept and each cost center can be edited separately again. New combinations from future syncs will show up as unconfigured.',
              'disable-confirm': 'Consider cost centers',
              'disable-success': 'Cost centers are considered in the mapping again',
              error: 'Could not save the setting',
            },
            'collapse-all': 'Collapse all',
            'expand-all': 'Expand all',
            'group-status-configured': 'All accounts configured',
            'group-status-partial': 'Some accounts not configured',
            'group-status-unconfigured': 'No accounts configured',
            'pending-configurations-tooltip': 'Pending configurations',
            'refresh-omie': 'Update Omie',
            'refresh-omie-in-progress': 'Updating Omie…',
            'refresh-sienge': 'Update Sienge',
            'refresh-sienge-in-progress': 'Updating Sienge…',
          },
          'expected-chart-accounts': {
            title: 'Expected Chart of Accounts',
            description: 'Manage expected custom chart account records.',
            create: 'New',
            'create-title': 'New record',
            'edit-title': 'Edit',
            format: 'Format',
            name: 'Name',
            'description-field': 'Description',
            identifier: 'Identifier',
            'raw-value': 'Raw value',
            'reference-date': 'Reference date',
            'numeric-value': 'Numeric value',
            actions: 'Actions',
            'no-data': 'No records found.',
            cancel: 'Cancel',
            save: 'Save',
            edit: 'Edit',
            'delete-title': 'Delete record',
            'delete-message': 'Are you sure you want to delete this record? This action can be reverted later.',
            delete: 'Delete',
            required: 'Required field',
            'numeric-required': 'Numeric value is required',
            'save-success': 'Record updated successfully.',
            'create-success': 'Record created successfully.',
            'save-error': 'Error saving.',
            'delete-success': 'Record deleted successfully.',
            'delete-error': 'Error deleting.',
            'cost-center': 'Cost center',
            category: 'Category',
            back: 'Scenarios',
            'scenario-default': 'Default',
            'scenario-default-description': 'Records with no associated scenario',
            'scenario-new': 'New Scenario',
            'scenario-create-title': 'New scenario',
            'scenario-edit-title': 'Edit scenario',
            'scenario-name': 'Name',
            'scenario-description': 'Description',
            'scenario-type': 'Type',
            'scenario-type-de': 'De',
            'scenario-type-para': 'Para',
            'scenario-type-cost-center': 'Cost center',
            'scenario-configuration': 'Configuration',
            'scenario-save-success': 'Scenario updated successfully.',
            'scenario-create-success': 'Scenario created successfully.',
            'scenario-save-error': 'Error saving scenario.',
            'scenario-delete-title': 'Delete scenario',
            'scenario-delete-message': 'Are you sure you want to delete this scenario? This action cannot be performed if there are associated records.',
            'scenario-delete-success': 'Scenario deleted successfully.',
            'scenario-delete-error': 'Error deleting scenario.',
            'scenario-no-data': 'No scenarios found.',
            search: 'Search',
            'no-search-results': 'No results match the search.',
            'detail-description': 'Expected account records for this scenario',
            'month-from': 'Month From',
            'month-to': 'Month To',
            'bulk-delete': 'Bulk delete',
            'bulk-delete-title': 'Bulk delete records',
            'bulk-delete-message': 'Are you sure you want to delete the selected records?',
            'bulk-delete-all-message': 'Are you sure you want to delete all records from this scenario?',
            'bulk-delete-count': 'Delete selected',
            'bulk-delete-all-count': 'Delete all',
            'bulk-delete-success': 'Records deleted successfully.',
            'bulk-delete-error': 'Error deleting records in bulk.',
            'bulk-page-selected': 'records on this page selected.',
            'bulk-select-all': 'Select all records',
            'bulk-all-selected': 'All records will be deleted.',
            'bulk-deselect': 'Deselect',
          },
          transactions: {
            title: 'Detailed Transactions',
            description: 'View all financial transactions',
            'button-text': 'View Detailed Transactions',
            'search-placeholder': 'Search transactions...',
            'date-from': 'Date From',
            'date-to': 'Date To',
            'reference-date': 'Date',
            name: 'Name',
            'numeric-value': 'Value',
            'supplier-name': 'Supplier Name',
            'category-name': 'Category Name',
            'category-identifier': 'Category Identifier',
            'original-category': 'Original Category',
            'original-category-name': 'Original Category Name',
            'custom-category': 'Custom Category',
            'custom-category-name': 'Custom Category Name',
            actions: 'Actions',
            'no-data': 'No transactions found',
            loading: 'Loading transactions...',
            'custom-category-indicator': '= Custom Category',
            'show-only-custom-category': 'Show only custom category',
            'custom-rules': {
              title: 'Custom Category Rules',
              add: 'Add Custom Rule',
              edit: 'Edit Custom Rule',
              remove: 'Remove Custom Rule',
              'remove-confirm': 'Are you sure you want to remove this custom rule?',
              'original-category': 'Original Category',
              'custom-category': 'Custom Category',
              'category-name': 'Category Name',
              'supplier-name': 'Supplier Name',
              'category-identifier': 'Category Identifier',
              'identifier-tooltip': 'The identifier cannot match an existing category',
              selected: 'Selected Category',
              save: 'Save',
              cancel: 'Cancel',
              required: 'Required field',
              'identifier-exists': 'This identifier already exists in configured categories',
              'category-name-exists': 'A category with this name already exists',
              'save-success': 'Custom rule saved successfully',
              'save-error': 'Error saving custom rule',
              'delete-success': 'Custom rule removed successfully',
              'delete-error': 'Error removing custom rule',
            },
          },
        },
        indicators: {
          tabs: {
            'kpi-table-configuration': {
              title: 'Tables',
              header: {
                'create-btn': {
                  text: 'New Data Table',
                },
                'search-input': {
                  placeholder: 'Search by Name...',
                },
                'order-tables-btn': {
                  text: 'Order Tables',
                  dialog: {
                    text: 'Define Order of Tables',
                    save: 'Save',
                    cancel: 'Cancel',
                  },
                },
              },
              form: {
                'create-title': {
                  text: 'New Data Table',
                },
                'edit-title': {
                  text: 'Edit Data Table',
                },
                fields: {
                  name: {
                    placeholder: 'Name',
                    'required-message': 'Name is required',
                  },
                  indicator: {
                    placeholder: 'Select Data',
                  },
                },
                save: {
                  text: 'Save',
                },
                cancel: {
                  text: 'Cancel',
                },
              },
              delete: {
                'confirmation-text': 'Confirm Delete?',
                text: 'Delete',
              },
              table: {
                column: {
                  name: {
                    text: 'Table',
                  },
                  format: {
                    text: 'Type',
                  },
                  actions: {
                    text: 'Actions',
                  },
                },
              },
            },
            'kpi-configuration': {
              title: 'Registered Data',
              header: {
                'create-btn': {
                  text: 'Register New Data',
                },
                'search-input': {
                  placeholder: 'Search by Name...',
                },
              },
              form: {
                'create-title': {
                  text: 'New data Entry',
                },
                'edit-title': {
                  text: 'Edit Data',
                },
                fields: {
                  name: {
                    placeholder: 'Name',
                    'required-message': 'Name is required',
                  },
                  format: {
                    placeholder: 'Type',
                    'required-message': 'Type is required',
                  },
                },
                save: {
                  text: 'Save',
                },
                cancel: {
                  text: 'Cancel',
                },
                delete: {
                  text: 'DELETE',
                  confirmation: {
                    title: 'Confirm Delete?',
                    confirm: 'DELETE',
                    cancel: 'CANCEL',
                  },
                },
              },
              table: {
                column: {
                  name: {
                    text: 'Registered Data',
                  },
                  format: {
                    text: 'Type',
                  },
                  actions: {
                    text: 'Actions',
                  },
                },
              },
            },
            'expected-scenarios': {
              'tab': 'Scenarios',
              'title': 'Complementary Data Scenarios',
              'description': 'Register budget, target or forecast scenarios for your complementary data and fill them by spreadsheet',
              'search': 'Search scenarios',
              'new': 'New Scenario',
              'name': 'Name',
              'observation': 'Notes',
              'values-count': 'Registered values',
              'actions': 'Actions',
              'edit': 'Edit',
              'delete': 'Delete',
              'no-data': 'No scenario registered yet.',
              'no-search-results': 'No scenario matches the search.',
              'create-title': 'New scenario',
              'edit-title': 'Edit scenario',
              'save': 'Save',
              'cancel': 'Cancel',
              'required': 'Required field',
              'duplicate-name': 'A scenario with this name already exists',
              'save-success': 'Scenario saved',
              'save-error': 'Error saving the scenario',
              'delete-title': 'Delete scenario',
              'delete-message': 'Are you sure you want to delete this scenario? It can only be deleted when it has no values.',
              'delete-blocked': 'This scenario still has values. Clear them by spreadsheet before deleting it.',
              'delete-success': 'Scenario deleted',
              'delete-error': 'Error deleting the scenario',
              'detail-description': 'Expected values of this scenario',
              'detail-empty': 'No values yet. Download the template, fill it in and upload it.',
              'indicator': 'Complementary data',
              'type': 'Type',
              'download-template': 'Download template',
              'download': 'Download',
              'download-error': 'Error generating the template',
              'start-month': 'Start month',
              'end-month': 'End month',
              'indicators': 'Complementary data',
              'indicators-hint': 'Leave empty to include all numeric complementary data',
              'upload': 'Upload spreadsheet',
              'file': 'Select the XLSX file',
              'upload-error': 'Error processing the spreadsheet',
              'upload-success': 'Spreadsheet applied',
              'preview-title': 'Spreadsheet preview',
              'preview-no-changes': 'The spreadsheet does not change any value.',
              'confirm': 'Confirm',
              'summary-created': 'New',
              'summary-updated': 'Changed',
              'summary-deleted': 'Removed',
              'invalid-title': 'The spreadsheet could not be imported',
              'invalid-cells': 'These cells are not valid numbers:',
              'template-invalid': 'The spreadsheet does not follow the template:',
              'ok': 'OK',
            },
          },
        },
        accounting: {
          title: 'Accounting',
          form: {
            'accounting-title': 'Fill in the data of your accountant',
            'accounting-title-filled': 'Accountant information',
            'accounting-title-edit': 'Edit Accountant Information',
            'accounting-text': 'We will request the data to implement the accounting module!',
            'accounting-text-filled': 'See or edit the data of the accountant registered for the accounting module.',
            'accounting-cnpj': 'Accounting Office CNPJ',
            'accountant-email': 'Accountant E-mail',
            'company-cnpj': 'Confirm Your Company CNPJ*',
            'accountant-phone': 'Accountant Phone',
            'cnpj-error-incomplete': 'Incomplete CNPJ',
            'cnpj-error-more-digits': 'CNPJ with more digits',
            'email-error-invalid': 'Invalid e-mail',
            'phone-error-incomplete': 'Phone incomplete',
            'phone-error-more-digits': 'Phone with more digits',
            'form-error-invalid': 'Please fill in all required fields correctly.',
            'save-success': 'Configuration saved successfully!',
            'save-error': 'Error saving configuration!',
            'add-email': 'Add Email',
            edit: 'Edit',
            cancel: 'Cancel',
            save: 'Save',
          },
          'configuration-in-process': {
            title: 'Configuration in progress',
            text: 'Your accounting module configuration is in progress. You will be notified when it is complete.',
            button: 'Go Back to Configuration',
          },
        },
      },
      dataIterator: {
        rowsPerPageText: 'Items per page:',
        pageText: '{0}-{1} of {2}',
        loadingText: 'Loading items...',
      },
      dataFooter: {
        itemsPerPageText: 'Items per page:',
        pageText: '{0}-{1} of {2}',
        itemsPerPageAll: 'All',
        nextPage: 'Next page',
        prevPage: 'Previous page',
        lastPage: 'Last page',
        firstPage: 'First page',
      },
      resources: {
        menus: {
          title: 'Navigation',
        },
        dashboard: {
          title: 'Home',
          read: 'View Home',
        },
        portfolio: {
          title: 'Portfolio',
          read: 'View Portfolio',
        },
        cockpit: {
          title: 'Cockpit',
          read: 'View Cockpit',
          summary: {
            title: 'Summary',
            read: 'View Summary',
          },
          analysis: {
            title: 'Analysis',
            read: 'View Analysis',
            accounting: {
              title: 'Accounting',
              read: 'View Accounting module',
            },
            financial: {
              title: 'Financial',
              read: 'View Financial module',
              summary: {
                read: 'View Summary',
              },
              receipts: {
                title: 'Receipts',
                read: 'View Receipts',
              },
              payments: {
                title: 'Payments',
                read: 'View Payments',
              },
              budget: {
                title: 'Budget',
                read: 'View Budget',
              },
              'cash-flow-statement': {
                title: 'Cash Flow Statement',
                read: 'View Cash Flow Statement',
              },
            },
            sales: {
              title: 'Sales',
              read: 'View Sales module',
              summary: {
                read: 'View Summary',
              },
              ticket: {
                title: 'Tickets',
                read: 'View Ticket Breakdown',
              },
              product: {
                title: 'Products',
                read: 'View Product Portfolio',
              },
              'sales-team': {
                title: 'Sales Team',
                read: 'View Sales Team',
              },
            },
            indicators: {
              title: 'Performance Data',
              read: 'View Performance Data module',
              new: 'Add Monthly Data',
              edit: 'Edit indicator for reference month',
              delete: 'Delete indicator for reference month',
              'upload-spreadsheet': 'Upload indicator spreadsheet',
            },
            reports: {
              title: 'Reports',
              read: 'View Reports module',
              new: 'Create new report',
              edit: 'Edit report',
              delete: 'Delete report',
            },
            operations: {
              title: 'Operations',
              read: 'View Operations module',
              inventory: {
                read: 'Visualizar Inventário',
              },
              summary: {
                read: 'Visualizar Resumo',
              },
            },
            funding: {
              title: 'Fundraising & IR',
              read: 'View Fundraising & IR module',
              new: 'Create Cap Table',
              edit: 'Edit Cap Table',
              delete: 'Delete Cap Table',
            },
          },
          alerts: {
            title: 'Alerts',
            read: 'View Alerts',
          },
        },
        dataroom: {
          title: 'Dataroom',
          read: 'View Dataroom',
        },
        'company-area': {
          title: 'Company Area',
          read: 'View Company Area',
          operations: {
            title: 'Operations',
            read: 'View Operations',
          },
          sales: {
            title: 'Sales',
            read: 'View Sales',
          },
          analysis: {
            title: 'Analysis',
            read: 'View Analysis',
          },
        },
        configuration: {
          title: 'Configuration',
          read: 'View Configuration',
          modules: {
            title: 'Modules Configuration',
            read: 'View Modules Configuration',
            accounting: {
              title: 'Accounting Configuration',
              read: 'View Accounting Configuration',
              new: 'Create Accounting Configuration',
              edit: 'Edit Accounting Configuration',
              delete: 'Delete Accounting Configuration',
            },
            financial: {
              title: 'Financial Configuration',
              read: 'View Financial Configuration',
              new: 'Create Financial Configuration',
              edit: 'Edit Financial Configuration',
              delete: 'Delete Financial Configuration',
              'view-chart-accounts': 'View Custom Chart Accounts',
              'edit-chart-accounts': 'Edit Custom Chart Accounts',
            },
            sales: {
              title: 'Sales Configuration',
              read: 'View Sales Configuration',
              new: 'Create Sales Configuration',
              edit: 'Edit Sales Configuration',
              delete: 'Delete Sales Configuration',
            },
            dataroom: {
              title: 'Dataroom Configuration',
              read: 'View Dataroom Configuration',
              new: 'Create Dataroom',
              edit: 'Edit Dataroom',
              delete: 'Delete Dataroom',
            },
            indicators: {
              title: 'Performance Data Configuration',
              read: 'View Performance Data Configuration',
              new: 'Create Performance Data',
              edit: 'Edit Performance Data',
              delete: 'Delete Performance Data',
              'expected-scenarios': 'Manage Complementary Data Scenarios',
            },
            'company-area': {
              title: 'Configuração da Área da Empresa',
              read: 'Visualizar Configuração da Área da Empresa',
              new: 'Criar Área da Empresa',
              edit: 'Editar Área da Empresa',
              delete: 'Deletar Área da Empresa',
            },
          },
          alerts: {
            title: 'Alerts Configuration',
            read: 'View Alerts Configuration',
            new: 'Create Alert',
            edit: 'Edit Alert',
            delete: 'Delete Alert',
          },
          users: {
            title: 'Users Configuration',
            read: 'View Users Configuration',
            new: 'Create new user',
            edit: 'Edit user',
            delete: 'Delete user',
          },
          roles: {
            title: 'Roles Configuration',
            read: 'View Roles Configuration',
            new: 'Create new role',
            edit: 'Edit role',
            delete: 'Delete role',
          },
          'company-area': {
            title: 'Company Area',
            description: 'Configure dashboards and company operation areas',
            'dashboard-type': {
              title: 'Dashboard Type',
              select: 'Select dashboard type',
              'selected-type': 'Selected type: {type}',
              'no-type-selected': 'None',
            },
            'operation-area': {
              title: 'Operation Area',
              select: 'Select operation area',
              'selected-area': 'Selected area: {area}',
              'no-area-selected': 'None',
              options: {
                operacao: 'Operation',
                comercial: 'Commercial',
                analise: 'Analysis',
              },
            },
            'select-type': {
              title: 'Select a dashboard type',
              description: 'Choose between Metabase, Google Sheets or Public URL to configure company dashboards.',
            },
            dashboards: {
              title: 'Dashboard Configuration',
              messages: {
                'save-success': 'Configuration saved successfully',
                'save-error': 'Error saving configuration',
                'test-success': 'Connection test successful',
                'test-error': 'Connection test failed',
                'remove-success': 'Configuration removed successfully',
                'remove-error': 'Error removing configuration',
              },
              metabase: {
                title: 'Metabase',
                url: 'Metabase URL',
                urlPlaceholder: 'https://your-metabase.com',
                apiKey: 'API Key',
                apiKeyPlaceholder: 'Enter your Metabase API key',
                dashboardId: 'Dashboard ID',
                dashboardIdPlaceholder: 'Ex: 1',
                save: 'Save',
              },
              googleSheets: {
                title: 'Google Sheets',
                url: 'Google Sheets URL',
                urlPlaceholder: 'https://docs.google.com/spreadsheets/d/...',
                sheetName: 'Sheet Name',
                sheetNamePlaceholder: 'Ex: Sheet1',
                range: 'Range (optional)',
                rangePlaceholder: 'Ex: A1:D10',
                save: 'Save',
              },
              publicUrl: {
                title: 'Public URL',
                url: 'Public URL',
                urlPlaceholder: 'https://example.com/dashboard',
                titleField: 'Title',
                titlePlaceholder: 'Dashboard Name',
                save: 'Save',
              },
              saved: {
                title: 'Saved Configurations',
                empty: {
                  title: 'No configurations saved yet.',
                  subtitle: 'Add dashboards above to get started.',
                },
                test: 'Test',
                remove: 'Remove',
                types: {
                  metabase: 'Metabase',
                  'google-sheets': 'Google Sheets',
                  'public-url': 'Public URL',
                },
              },
            },
          },
        },
      },
      warning: {
        permission: {
          text: 'You do not have permission to access the contents of this page. Please contact your account manager.',
        },
      },
    },
  },
  pt_BR: {
    '10': '10',
    '25': '25',
    '50': '50',
    '100': '100',
    common: {
      client: {
        title: 'Cliente',
      },
    },
    notifications: {
      title: 'Notificações',
      markAllAsRead: 'Limpar',
      noNotifications: 'Nenhuma notificação nova',
    },
    $vuetify: {
      diagnostico: {
        title: 'Diagnóstico',
        subtitle: 'Entenda automaticamente para onde o caixa foi no período selecionado.',
        email: {
          button: 'Enviar por e-mail',
          title: 'Enviar por e-mail',
          subtitle: 'Envie este diagnóstico para um ou mais destinatários.',
          recipients: 'Destinatários',
          // eslint-disable-next-line @stylistic/quotes -- see the note on the English copy.
          'recipients-placeholder': "nome{'@'}empresa.com",
          'recipients-hint': 'Pressione Enter para adicionar cada e-mail.',
          message: 'Mensagem (opcional)',
          'message-placeholder': 'Escreva uma nota para acompanhar o diagnóstico...',
          send: 'Enviar',
          cancel: 'Cancelar',
          done: 'Concluir',
          'sent-title': 'Diagnóstico enviado',
          'sent-body': 'Enviado para {0} destinatário(s).',
          'error-empty': 'Adicione ao menos um destinatário.',
          'error-invalid': 'E-mail inválido: {0}',
          'error-failed': 'Não foi possível enviar. Tente novamente.',
        },
        history: {
          title: 'Diagnósticos anteriores',
          subtitle: 'Cada diagnóstico gerado fica salvo aqui. Clique para reabrir.',
          open: 'Anteriores',
          close: 'Fechar histórico',
          empty: 'Você ainda não gerou nenhum diagnóstico para este cliente.',
        },
        types: {
          'fluxo-caixa': 'Fluxo de Caixa',
          resultado: 'Resultado',
          'contas-pagar': 'Contas a Pagar',
          'coming-soon': 'Em breve',
        },
        filters: {
          type: 'Tipo de diagnóstico',
          'date-from': 'Data inicial',
          'date-to': 'Data final',
          generate: 'Gerar Diagnóstico',
          generating: 'Gerando...',
        },
        generating: {
          title: 'Gerando seu diagnóstico',
          subtitle: 'Estamos analisando os dados da empresa para gerar uma leitura executiva do período.',
          collecting: 'Coletando dados do período...',
          computing: 'Calculando variações e indicadores...',
          analyzing: 'Identificando alertas e oportunidades...',
          writing: 'Redigindo o diagnóstico executivo...',
        },
        quota: {
          cooldown: 'Aguarde {0}s para gerar um novo diagnóstico.',
          daily_limit: 'Você atingiu o limite de {0} diagnósticos por dia. Fale com o suporte para ampliar.',
          monthly_limit: 'Você atingiu o limite de {0} diagnósticos neste mês.',
          concurrent: 'Já existe um diagnóstico sendo gerado.',
          token_cap: 'O limite de uso de IA da sua empresa foi atingido neste mês.',
          cost_cap: 'O limite de uso de IA da sua empresa foi atingido neste mês.',
          remaining: '{0} de {1} diagnósticos usados hoje',
          'ai-off': 'Análise automática indisponível - exibindo a leitura calculada.',
        },
        result: {
          'cash-grew': 'Seu caixa cresceu',
          'cash-shrank': 'Seu caixa encolheu',
          'in-period': 'no período',
          'cash-will-grow': 'Seu caixa deve crescer',
          'cash-will-shrink': 'Seu caixa deve encolher',
          'by-period-end': 'até o fim do período',
          'closing-balance-projected': 'Saldo Final Projetado',
          'opening-balance': 'Saldo Inicial',
          'closing-balance': 'Saldo Final',
          timeline: {
            title: 'Linha do tempo do caixa',
            subtitle: 'Como o saldo se comportou dia a dia e as principais entradas e saídas do período.',
            inflow: 'Entrada',
            outflow: 'Saída',
            endpoints: 'Saldo inicial/final',
            start: 'Início',
            end: 'Fim',
          },
          negative: {
            'title-one': 'Seu caixa ficou negativo por {0} dias',
            'title-many': 'Seu caixa ficou negativo em {0} momentos ({1} dias no total)',
            'body-one': 'Entre {0} e {1} o saldo esteve abaixo de zero.',
            'body-many': 'O saldo entrou e saiu do vermelho {0} vezes no período - um sinal de descasamento recorrente entre contas a pagar e a receber.',
            worst: 'O ponto mais crítico foi {0} em {1}.',
            'days-in-red': 'Dias no vermelho',
            'worst-balance': 'Pior saldo',
            'drill-down': 'Ver Fluxo de Caixa do Período',
          },
          suggestions: {
            title: 'Como evitar o caixa negativo: sugestões de reagendamento',
            subtitle: 'Pequenos ajustes nas datas de pagamentos e recebimentos manteriam o saldo sempre acima de zero.',
          },
          'top-movements': {
            title: 'Maiores movimentações do período',
            subtitle: 'Os maiores recebimentos e pagamentos, sem as transferências entre suas próprias contas.',
            'subtitle-projected': 'Os maiores recebimentos e pagamentos, incluindo os projetados a partir de {0}, sem as transferências entre suas próprias contas.',
            projected: 'Projetado',
            inflows: 'Recebimentos',
            outflows: 'Pagamentos',
            'no-inflows': 'Nenhum recebimento no período.',
            'no-outflows': 'Nenhum pagamento no período.',
          },
          observations: {
            title: 'Outras observações',
            badge: 'Beta',
            subtitle: 'Padrões encontrados no período que não dependem de reagendar nada.',
          },
          'no-data': {
            title: 'Sem dados de fluxo de caixa para este período',
            subtitle: 'Verifique as datas selecionadas, ou se este cliente possui dados financeiros sincronizados.',
          },
          'projected-from': 'A partir de {0} os saldos são projetados, não realizados.',
          'internal-transfers': '{0} transferências internas entre suas próprias contas foram ignoradas.',
          'generated-at': 'Gerado em {0}',
          refresh: 'Atualizar',
        },
        error: {
          title: 'Não foi possível gerar o diagnóstico',
          retry: 'Tentar novamente',
          generic: 'Algo deu errado. Tente novamente.',
        },
      },
      reports: {
        templates: {
          'field-types': {
            quantity: 'Número',
            brl: 'BRL (R$)',
            usd: 'USD (US$)',
            ratio: 'Percentual (%)',
            short_text: 'Texto curto',
            long_text: 'Texto longo',
            dropdown: 'Lista suspensa',
            attachment: 'Anexo',
          },
          attachment: {
            pick: 'Adicionar arquivos',
            empty: 'Nenhum arquivo anexado.',
            'upload-error': 'Não foi possível enviar o arquivo. Tente novamente.',
            'download-error': 'Não foi possível abrir o arquivo. Tente novamente.',
          },
          builder: {
            'field-label': 'Rótulo do campo',
            'field-type': 'Tipo',
            'field-required': 'Obrigatório',
            'field-options': 'Opções',
            'field-options-hint': 'Pressione Enter para adicionar cada opção',
            'field-description': 'Descrição (opcional)',
            'field-description-hint': 'Exibida como dica para quem preencher o report',
            'add-field': 'Adicionar campo',
          },
          renderer: {
            empty: 'Este modelo ainda não possui campos.',
            required: 'Campo obrigatório',
          },
          visibility: {
            public: 'Público',
            restricted: 'Restrito ao portfólio',
          },
          list: {
            title: 'Modelos de relatório',
            back: 'Voltar',
            create: 'Novo modelo',
            'delete-title': 'Apagar modelo?',
            'delete-text': 'O modelo "{0}" deixará de estar disponível para novos relatórios.',
            'delete-hint': 'Os relatórios já criados a partir dele, publicados ou em rascunho, continuam como estão e seguem mostrando o nome do modelo.',
            'delete-confirm': 'Apagar',
            columns: {
              name: 'Nome',
              description: 'Descrição',
              fields: 'Campos',
              visibility: 'Visibilidade',
              actions: 'Ações',
            },
          },
          form: {
            'title-create': 'Novo modelo',
            'title-edit': 'Editar modelo',
            name: 'Nome do modelo',
            description: 'Descrição',
            'public-label': 'Tornar modelo público',
            'public-on': 'Público — disponível para todos os clients.',
            'public-off': 'Restrito — aparece apenas para os clients presentes no seu portfólio.',
            'fields-title': 'Campos',
            cancel: 'Cancelar',
            save: 'Salvar',
            reminders: {
              title: 'Lembretes de vencimento',
              hint: 'Lembre as empresas do seu portfólio de preencher este report antes do vencimento. Quem já publicou o report do período não recebe lembrete.',
              enabled: 'Enviar lembretes de vencimento',
              recurring: 'Report recorrente',
              'recurring-on': 'Vence a cada período, no dia abaixo do mês seguinte ao fim do período.',
              'recurring-off': 'Vence uma única vez, numa data específica.',
              recurrence: 'Frequência',
              monthly: 'Mensal',
              quarterly: 'Trimestral',
              yearly: 'Anual',
              'due-day': 'Dia do vencimento',
              'due-day-hint': 'Dia do mês seguinte ao fechamento do período (1–31).',
              'due-date': 'Data de vencimento',
              offsets: 'Quando lembrar',
              'offsets-hint': 'Escolha um ou mais.',
              'offset-before': '{0} dias antes',
              'offset-before-one': '1 dia antes',
              'offset-today': 'No dia do vencimento',
              'offset-after': '{0} dias depois',
              'offset-after-one': '1 dia depois',
              channels: 'Enviar por',
              'channel-in-app': 'Notificação no Cockpit',
              'channel-email': 'E-mail',
              required: 'Obrigatório',
              'due-day-range': 'Use um dia entre 1 e 31',
              'pick-one': 'Escolha pelo menos um',
            },
          },
          published: {
            title: 'Relatórios publicados',
            back: 'Voltar',
            close: 'Fechar',
            tooltip: 'Ver relatórios publicados',
            export: 'Exportar Excel',
            'export-error': 'Não foi possível exportar o relatório. Tente novamente.',
            columns: {
              template: 'Modelo',
              name: 'Nome',
              'reference-date': 'Data de referência',
              'published-at': 'Publicado em',
              actions: 'Ações',
            },
          },
          client: {
            'tab-title': 'Modelos',
            'available-title': 'Modelos disponíveis',
            'my-reports-title': 'Meus relatórios',
            fill: 'Preencher',
            status: {
              draft: 'Rascunho',
              published: 'Publicado',
            },
            columns: {
              name: 'Nome',
              description: 'Descrição',
              fields: 'Campos',
              template: 'Modelo',
              'reference-date': 'Data de referência',
              status: 'Situação',
              actions: 'Ações',
            },
          },
          fill: {
            title: 'Preencher relatório',
            name: 'Título do relatório',
            'reference-date': 'Data de referência',
            cancel: 'Cancelar',
            'save-draft': 'Salvar rascunho',
            publish: 'Publicar',
            'publish-title': 'Publicar relatório',
            'publish-hint': 'Escolha as organizações que poderão ver este relatório.',
            'no-organizations': 'Nenhuma organização disponível para publicação.',
            organizations: 'Organizações',
            'confirm-publish': 'Publicar',
            'required-error': 'Preencha todos os campos obrigatórios antes de publicar.',
            'published-locked': 'Este relatório está publicado e não pode mais ser editado. Você ainda pode enviá-lo para outras organizações.',
            'template-unavailable': 'Este modelo não está mais disponível para novos relatórios.',
            share: 'Enviar para outras organizações',
            'share-title': 'Enviar para outras organizações',
            'share-hint': 'As organizações que já receberam este relatório continuam marcadas. Só as que você adicionar agora serão notificadas.',
            'confirm-share': 'Enviar',
          },
        },
      },
      open: 'abrir',
      close: 'fechar',
      noDataText: 'Nenhum registro encontrado',
      enabled: 'Habilitado',
      disabled: 'Desabilitado',
      details: 'Detalhes',
      'go-back-btn': {
        text: 'Voltar',
      },
      layout: {
        menu: {
          diagnostico: {
            title: 'Diagnóstico',
          },
          dashboard: {
            title: 'Home',
          },
          'create-client': {
            title: '+ Criar Novo Cliente',
          },
          // TECH-2671 — sufixo da conta pessoal no seletor de contas.
          'my-portfolio': 'Meu Portfólio',
          myCompanies: {
            title: 'Empresas',
          },
          cockpit: {
            title: 'Cockpit',
          },
          configuration: {
            title: 'Configuração',
          },
          dataroom: {
            title: 'Dataroom',
          },
          'company-area': {
            title: 'Área da Empresa',
            description: 'Configure dashboards e áreas de operação da empresa',
          },
          portfolio: {
            title: 'Portfólio',
          },
          profile: {
            title: 'Perfil',
          },
          contact: {
            title: 'Contato',
          },
          logout: {
            title: 'Logout',
          },
        },
      },
      'access-denied': {
        title: 'Acesso negado',
        message: 'Você não tem permissão para acessar este cliente. Por favor, contate seu gerente de conta.',
        logout: 'Logout',
      },
      portfolio: {
        title: 'Portfólio',
        'search-placeholder': 'Buscar por nome ou CNPJ da empresa...',
        'export-button': 'Exportar respostas',
        'company-users': {
          button: 'Usuários',
          title: 'Usuários da empresa',
          subtitle: 'Pessoas de fora da sua empresa que podem acompanhar uma empresa do portfólio. Usuários convidados veem apenas as empresas para as quais foram convidados.',
          'company-select': 'Empresa',
          'select-hint': 'Selecione uma empresa para ver e convidar seus usuários.',
          'personal-account-blocked': 'Conta pessoal não gerencia usuários de empresa: ela acompanha o portfólio de outras empresas e não convida ninguém.',
          'search-placeholder': 'Buscar por nome ou e-mail...',
          'invite-button': 'Convidar usuário',
          empty: 'Nenhum usuário ainda',
          cancel: 'Cancelar',
          error: 'Algo deu errado. Tente novamente.',
          columns: {
            name: 'Nome',
            email: 'E-mail',
          },
          remove: {
            action: 'Remover acesso',
            title: 'Remover o acesso deste usuário à empresa?',
            confirm: 'Remover',
            success: 'Acesso removido',
          },
          'pending-invites': {
            title: 'Convites pendentes',
            cancel: {
              action: 'Cancelar convite',
              title: 'Cancelar este convite?',
              confirm: 'Cancelar convite',
            },
          },
          invite: {
            title: 'Convidar usuário',
            name: 'Nome',
            email: 'E-mail',
            hint: 'O convite é enviado por e-mail. Ao entrar no Cockpit, o usuário vê apenas esta empresa no Portfólio.',
            send: 'Enviar convite',
            required: 'Obrigatório',
            'invalid-email': 'E-mail inválido',
            success: 'Convite enviado',
            error: 'Não foi possível enviar o convite',
          },
        },
        'company-invite': {
          title: 'Convite para o Portfólio',
          message: 'Você foi convidado para acompanhar esta empresa no Portfólio do Cockpit:',
          accept: 'Aceitar',
          decline: 'Recusar',
        },
        answers: {
          title: 'Respostas',
          'reports-button': 'Relatórios',
          'templates-button': 'Modelos',
          'export-error': 'Não foi possível exportar as respostas. Tente novamente.',
          'no-selection-warning': 'Selecione os relatórios que deseja exportar',
          'selected-count': '{count} relatório(s) selecionado(s)',
          'select-all': 'Selecionar todos ({count})',
          'clear-selection': 'Limpar seleção',
          columns: {
            company: 'Empresa',
            document: 'CNPJ',
            template: 'Modelo',
            'reference-date': 'Data de Referência',
            'published-at': 'Data de Publicação',
            actions: 'Ações',
          },
          filters: {
            template: 'Modelo',
            'reference-date': 'Data de Referência',
            'publication-date': 'Data de Publicação',
            from: 'De',
            to: 'Até',
          },
        },
        table: {
          column: {
            name: 'Nome',
            segment: 'Segmento',
            modules: 'Módulos',
            actions: 'Ações',
          },
          modules: {
            reports: 'Reports',
            dataroom: 'Dataroom',
            accounting: 'Contábil',
            captable: 'Captable',
            financial: 'Financeiro',
            'company-area': 'Área da Empresa',
          },
          'dataroom-tooltip': 'Ir para o dataroom',
        },
      },
      'my-companies': {
        home: {
          title: {
            text: 'Empresas',
          },
          'card-header': {
            'search-input': {
              placeholder: 'Buscar por Nome ou CNPJ...',
            },
            'filter-companies-btn': {
              text: 'Filtrar Empresas',
            },
            'generate-cockpit-btn': {
              text: 'Gerar Cockpit',
            },
          },
          table: {
            column: {
              name: {
                text: 'Nome',
              },
              'tax-id': {
                text: 'CNPJ',
              },
              segment: {
                text: 'Segmento',
              },
              group: {
                text: 'Grupo',
              },
              organization: {
                text: 'Organização',
              },
              'last-alerts': {
                text: 'Últimos alertas',
              },
              'updated-at': {
                text: 'Atualizado em',
              },
              actions: {
                text: 'Ações',
              },
            },
          },
        },
      },
      'create-client': {
        title: {
          text: 'Criar novo Cliente',
        },
        'invite-dialog': {
          title: 'Você recebeu um convite!',
          accept: 'Aceitar',
          decline: 'Recusar',
        },
        form: {
          name: {
            label: 'Nome',
            placeholder: 'Nome',
          },
          document: {
            label: 'CNPJ',
            placeholder: 'CNPJ',
            rules: {
              required: 'CNPJ é obrigatório',
              complete: 'CNPJ incompleto',
              invalid: 'CNPJ inválido',
            },
          },
          segment: {
            label: 'Segmento',
          },
          save: {
            text: 'Salvar',
          },
          cancel: {
            text: 'Cancelar',
          },
        },
      },
      cockpit: {
        'privacy-policy': {
          title: 'Política de Privacidade',
          label: 'Eu li e aceito a',
          link: 'Política de Privacidade do Cockpit',
          button: 'Aceito',
          update: {
            title: 'Atualização da Política de Privacidade',
            text: 'Atualizamos a política de privacidade. Por favor, revise-a e aceite-a para continuar usando o Cockpit.',
            link: 'Política de Privacidade do Cockpit',
            button: 'Aceito',
          },
        },
        tabs: {
          summary: {
            title: 'Resumo',
          },
          analysis: {
            title: 'Análise',
            cards: {
              'coming-soon': {
                title: 'Em Breve!',
                description: 'Seja o primeiro a saber quando estiver disponível',
              },
              accounting: {
                title: 'Contábil',
                description: 'Confira o Balanço Patrimonial, DRE e outras demonstrações contábeis da empresa',
              },
              financial: {
                title: 'Financeiro',
                description:
                  'Faça a análise de Fluxo de Caixa, DRE Gerencial, Burn Rate, Runway e outras métricas financeiras',
              },
              sales: {
                title: 'Comercial',
                description:
                  'Confira as métricas comerciais da empresa. Pipeline completo, Taxa de conversão, Churn Rate e muito mais',
              },
              kpis: {
                title: 'Dados Complementares',
                description: 'Adicione e monitore dados complementares da sua empresa',
              },
              reports: {
                title: 'Relatórios',
                description: 'Crie e gerencie os relatórios da sua empresa',
              },
              operations: {
                title: 'Análise Operacional',
                description: 'Controle as métricas das atividades operacionais da sua empresa',
              },
              funding: {
                title: 'Captação de Recursos & RI',
                description:
                  'Gerencie a captação de recursos e o relacionamento com investidores e credores. Crie um dataroom em tempo real.',
              },
            },
          },
          alerts: {
            title: 'Alertas',
          },
        },
        financial: {
          title: {
            text: 'Análise Financeira',
          },
          tabs: {
            cashflow: {
              title: 'Fluxo de Caixa',
            },
            'income-statement': {
              title: 'Demonstração de Fluxo de Caixa',
            },
            'consolidated-cash-balance': {
              title: 'Saldos de Caixa',
            },
            'other-indicators': {
              title: 'Outros Indicadores',
            },
            'expected-realized': {
              title: 'Orçado vs Realizado',
            },
            'burn-rate': {
              title: 'Burn Rate',
            },
            summary: {
              title: 'Resumo',
            },
            receipts: {
              title: 'Recebimentos',
            },
            payments: {
              title: 'Pagamentos',
            },
            budget: {
              title: 'Orçamento',
            },
            scenarios: {
              title: 'Cenários',
            },
            orcamento: {
              title: 'Gerenciar Orçamento',
              'add-btn': 'Adicionar',
              'upload-btn': 'Upload Planilha',
              'download-template': 'Baixar Template',
              indicator: 'Indicador',
              'cost-center': 'Centro de Custo',
              scenario: 'Cenário',
              'date-from': 'Data inicial',
              'date-to': 'Data final',
              name: 'Observação',
              identifier: 'Identificador',
              'reference-date': 'Data de referência',
              value: 'Valor',
              actions: 'Ações',
              'dialog-new': 'Novo orçamento',
              'dialog-edit': 'Editar orçamento',
              'dialog-delete-title': 'Remover orçamento',
              'dialog-delete-message': 'Tem certeza que deseja remover este orçamento?',
              'upload-title': 'Upload de Planilha',
              'upload-select': 'Selecione o arquivo XLSX',
              'upload-confirm': 'Fazer Upload',
              'preview-title': 'Preview da Planilha',
              'preview-confirm': 'Confirmar',
              'template-title': 'Baixar Template',
              'template-download': 'Baixar',
              cancel: 'Cancelar',
              save: 'Salvar',
              delete: 'Remover',
              'save-success': 'Orçamento salvo com sucesso!',
              'delete-success': 'Orçamento removido com sucesso!',
              'upload-success': 'Planilha enviada com sucesso!',
              'upload-error': 'Erro ao fazer upload da planilha',
              'template-success': 'Template baixado com sucesso!',
              'template-error': 'Erro ao baixar template',
            },
            runaway: {
              title: 'Runaway',
            },
            'cash-flow-projection': {
              title: 'Fluxo de Caixa Diário',
              'export-excel': {
                tooltip: 'Exportar para Excel',
                error: 'Não foi possível gerar a planilha. Tente novamente.',
                empty: 'Nada para exportar com os filtros selecionados.',
                truncated: 'Exportação limitada a 20.000 linhas. Reduza o período para obter o intervalo completo.',
                columns: {
                  date: 'Data',
                  type: 'Tipo',
                  'type-in': 'Entrada',
                  'type-out': 'Saída',
                  customer: 'Cliente/Fornecedor',
                  category: 'Categoria',
                  'cost-center': 'Centro de custo',
                  document: 'Documento',
                  description: 'Descrição',
                  'bank-account': 'Conta bancária',
                  'issue-date': 'Emissão',
                  'due-date': 'Vencimento',
                  'payment-date': 'Pagamento',
                  amount: 'Valor',
                  'original-amount': 'Valor original',
                  'paid-amount': 'Valor pago',
                  'remaining-amount': 'Em aberto',
                  balance: 'Saldo',
                  'external-id': 'ID Omie',
                  origin: 'Origem',
                  'origin-forecast': 'Previsão do Omie',
                },
              },
              'initial-balance': 'Saldo Inicial',
              'final-balance': 'Saldo Final',
              'loading-more': 'Carregando mais transações...',
              projected: 'Projetado',
              realized: 'Realizado',
              'movement-details': 'Detalhes da Movimentação',
              'forecast-badge': 'Previsto',
              'forecast-tooltip': 'Lançamento previsto no Omie — ainda não é um título a pagar ou a receber',
              origin: 'Origem',
              'origin-forecast': 'Previsão do Omie',
              'forecast-origins': {
                purchase_order: 'Pedido de compra (previsto)',
                purchase_invoice: 'Nota de entrada (prevista)',
                monthly_contract: 'Contrato mensal (previsto)',
                quote: 'Orçamento (previsto)',
              },
              client: 'Cliente',
              category: 'Categoria',
              account: 'Conta',
              value: 'Valor',
              'document-number': 'Número do Documento',
              'issue-date': 'Data de Emissão',
              'due-date': 'Data de Vencimento',
              'expected-payment-date': 'Previsão de Pagamento',
              'payment-date': 'Data de Pagamento',
              'original-amount': 'Valor Original',
              'paid-amount': 'Valor Pago',
              'remaining-amount': 'Valor Restante',
              'cost-center': 'Centro de Custo',
              description: 'Descrição',
              status: 'Status',
              pagination: {
                'page-info': 'Página {current} de {total}',
              },
              'empty-states': {
                'no-realized-transactions': 'Nenhuma transação realizada encontrada',
                'no-projected-transactions': 'Nenhuma transação projetada encontrada',
              },
              filters: {
                account: {
                  label: 'Conta',
                  'search-placeholder': 'Buscar conta...',
                  'selected-count': '{count} contas selecionadas',
                  'no-linked-account': 'Sem conta vinculada',
                },
                client: {
                  label: 'Cliente',
                  'search-placeholder': 'Buscar cliente...',
                  'selected-count': '{count} clientes selecionados',
                  'no-results': 'Nenhum resultado encontrado',
                  'search-hint': 'Digite para buscar clientes...',
                },
                'date-from': 'Data Inicial',
                'date-to': 'Data Final',
                buttons: {
                  cancel: 'Cancelar',
                  confirm: 'Confirmar',
                  apply: 'Aplicar',
                },
                validation: {
                  'invalid-date': 'Informe uma data válida',
                  'end-before-start': 'A data final não pode ser anterior à data inicial',
                },
              },
            },
            'cash-flow-table': {
              title: 'Tabela de Fluxo de caixa',
              loading: 'Carregando os dados de saldo necessários para exibir a demonstração de fluxo de caixa. Aguarde...',
              actions: {
                'from-to': {
                  text: 'De-Para',
                },
                'x-ray': {
                  text: 'Raio-X',
                  tabs: {
                    table: {
                      title: 'Tabela',
                    },
                    dashboard: {
                      title: 'Gráfico',
                    },
                  },
                },
              },
              'cash-flow-tables': {
                title: {
                  operations: 'Fluxo de Caixa Operacional',
                  financing: 'Fluxo de Caixa de Financiamento',
                  investment: 'Fluxo de Caixa de Investimento',
                  total: 'Fluxo de Caixa Total',
                  resume: 'Resumo',
                },
                rows: {
                  'cash-in': 'Entradas',
                  'cash-out': 'Saídas',
                  result: 'Resultado',
                  'total-cash-flow': 'Fluxo de Caixa Total',
                  'beginning-of-period': 'Saldo Inicial de Caixa',
                  'end-of-period': 'Saldo Final de Caixa',
                },
              },
              'from-to-table': {
                titles: {
                  'operations-cash-in': 'Entradas Operacionais',
                  'operations-cash-out': 'Saídas Operacionais',
                  'operations-result': 'Resultado Operacional',
                  'financing-cash-in': 'Entradas de Financiamento',
                  'financing-cash-out': 'Saídas de Financiamento',
                  'financing-result': 'Resultado de Financiamento',
                  'investment-cash-int': 'Saídas de Investimento',
                  'investment-cash-out': 'Saídas de Investimento',
                  'investment-result': 'Resultado de Investimento',
                  'total-cash-flow': 'Fluxo de Caixa Total',
                },
                cols: {
                  'category-identifier': 'Número da Conta',
                  account: 'Conta',
                  'client-account': 'Conta do Cliente',
                  type: 'Tipo',
                  value: 'Valor',
                },
                rows: {
                  total: 'Total',
                },
              },
            },
          },
        },
        indicators: {
          title: {
            text: 'Dados Complementares',
          },
          tabs: {
            evap: {
              title: 'EVAP',
            },
            kpi: {
              title: 'Raio-X do KPI',
              header: {
                'create-btn': {
                  text: 'Inserir Manualmente',
                },
                'upload-spreadsheet-btn': {
                  text: 'Enviar Planilha',
                },
                'spreadsheet-btn': {
                  text: 'Baixar Planilha Modelo',
                  'start-month': {
                    text: 'Mês Inicial',
                  },
                  'end-month': {
                    text: 'Mês Final',
                  },
                  select: {
                    'select-all': 'Selecionar Todos',
                    'select-indicators': 'Selecionar Dados',
                    error: 'Selecione pelo menos um indicador',
                  },
                  preview: {
                    title: 'Pré-visualização da Tabela de dados complementares',
                    tipo: 'Tipo',
                  },
                  confirm: {
                    text: 'Confirmar',
                  },
                  cancel: {
                    text: 'Cancelar',
                  },
                  download: {
                    text: 'Baixar',
                  },
                  upload: {
                    text: 'Enviar',
                    success: 'Dados complementares atualizados com sucesso',
                    'success.expected': 'Planilha Expected enviada com sucesso',
                    error: 'Planilha inválida',
                    invalid: 'Valores numéricos inválidos encontrados',
                    row: 'Linha',
                    column: 'Coluna',
                  },
                },
              },
              actions: {
                'x-ray': {
                  text: 'Raio-X',
                },
                edit: {
                  text: 'Editar',
                },
                delete: {
                  text: 'Deletar',
                },
              },
              'edit-form': {
                title: {
                  text: 'Atualizar Indicador de um mês',
                },
              },
              form: {
                title: {
                  text: 'Inserção Manual de Dados',
                },
                'redirect-indicator-configuration-btn': {
                  text: 'Cadastrar Novos Dados',
                },
                fields: {
                  'reference-date': {
                    placeholder: 'Mês de Referência',
                  },
                  indicator: {
                    placeholder: 'Dados',
                  },
                },
                save: {
                  text: 'Salvar',
                },
                cancel: {
                  text: 'Cancelar',
                },
              },
              'no-indicators-warning': {
                text: 'Não há Indicadores cadastrados',
                'insert-first': 'Indicadores não encontrados, cadastre o primeiro aqui',
              },
              loading: {
                text: 'Carregando indicadores…',
                'updating-text': 'Atualizando…',
              },
            },
            'kpi-table': {
              title: 'Dados Inseridos',
            },
            'daily-cash-flow': {
              title: 'Fluxo de Caixa Diário',
              'date-from': 'Data Início',
              'date-to': 'Data Fim',
              'no-data': 'Nenhum dado disponível para o período selecionado.',
              company: 'Empresa',
              'share-value': 'Participação',
              'total-value': '100%',
              ungrouped: 'Sem grupo',
              'unnamed-account': 'Sem conta bancária',
            },
          },
        },
        accounting: {
          title: {
            text: 'Análise Contábil',
          },
        },
        operations: {
          title: {
            text: 'Análise Operacional',
          },
          tabs: {
            summary: {
              title: 'Resumo',
            },
            inventory: {
              title: 'Inventário',
            },
          },
        },
        reports: {
          home: {
            title: 'Meus Relatórios',
            'create-report-btn': {
              text: 'Criar Relatório',
            },
            'create-dialog': {
              title: 'Criar Relatório',
              'from-scratch': {
                title: 'Criar do Zero',
                description: 'Monte um relatório a partir de um formulário em branco.',
              },
              'from-template': {
                title: 'Utilizar um Modelo',
                description: 'Comece a partir de um dos modelos disponíveis.',
              },
              'use-template': 'Utilizar Modelo',
              'coming-soon': 'Em breve',
              'no-templates': 'Nenhum modelo disponível.',
              fields: '{count} campos',
              owner: 'Modelo de {name}',
              back: 'Voltar',
              cancel: 'Cancelar',
            },
            tabs: {
              drafts: {
                title: 'Relatórios',
              },
              'real-estate': {
                title: 'Relatórios Imobiliários',
              },
            },
            'draft-list': {
              columns: {
                name: 'Nome',
                'reference-date': 'Data de Referência',
                'created-by': 'Criado Por',
                visibility: 'Visibilidade',
                actions: 'Ações',
              },
              visibility: {
                public: 'Público',
                private: 'Privado',
              },
            },
            pending: {
              title: 'Relatórios pendentes',
              subtitle: 'Atrasados ou com vencimento nos próximos {days} dias',
              period: 'Referente a {period}',
              'due-on': 'vence em {date}',
              'due-in': 'Vence em {days} dias',
              'due-in-one': 'Vence em 1 dia',
              'due-today': 'Vence hoje',
              overdue: 'Atrasado há {days} dias',
              'overdue-one': 'Atrasado há 1 dia',
              quarter: '{quarter}º trimestre de {year}',
              fill: 'Preencher',
              'continue-draft': 'Continuar rascunho',
              ignore: 'Ignorar lembrete',
              'ignore-hint': 'Ignorar o lembrete deste período',
              'ignore-title': 'Ignorar este lembrete?',
              'ignore-confirm': 'Você deixa de receber lembretes do relatório "{report}" referentes a {period}, aqui e por e-mail. Os outros períodos continuam sendo lembrados normalmente.',
              'ignore-confirm-action': 'Ignorar lembrete',
              'ignore-cancel': 'Cancelar',
            },
          },
          'reports-form': {
            title: {
              text: 'Criar Relatório',
            },
            'create-btn': {
              text: 'Criar Relatório',
            },
            'delete-draft-btn': {
              text: 'Deletar rascunho',
            },
            fields: {
              name: {
                label: 'Nome',
              },
              'reference-date': {
                label: 'Data de Referência',
              },
              observations: {
                label: 'Comentários',
              },
              public: {
                label: 'Público',
              },
            },
            modules: {
              indicators: {
                title: 'Indicadores',
                table: {
                  'indicator-tooltip': {
                    text: 'Clique para editar o valor do indicador no report',
                  },
                  'indicator-form-edit': {
                    title: 'Editar Dados',
                  },
                  'start-reference-date': {
                    placeholder: 'Mês Inicial',
                  },
                  'end-reference-date': {
                    placeholder: 'Mês Final',
                  },
                },
              },
              financial: {
                title: 'Financeiro',
                'cash-flow-table': {
                  title: 'Fluxo de Caixa',
                  tooltip: {
                    text: 'Clique para editar o valor do indicador no report',
                  },
                  'start-reference-date': {
                    placeholder: 'Mês Inicial',
                  },
                  'end-reference-date': {
                    placeholder: 'Mês Final',
                  },
                  'edit-form': {
                    title: 'Editar Fluxo de Caixa',
                    'reference-date': {
                      placeholder: 'Mês de Referência',
                    },
                    name: {
                      text: 'Conta',
                    },
                    value: {
                      text: 'Valor',
                    },
                    'save-btn': {
                      text: 'Salvar',
                    },
                    'cancel-btn': {
                      text: 'Cancelar',
                    },
                  },
                },
              },
              attachments: {
                title: 'Anexos',
                'upload-btn': {
                  text: 'Upload',
                },
                'upload-dialog': {
                  title: 'Upload de Anexos',
                  form: {
                    name: {
                      text: 'Nome',
                    },
                    description: {
                      text: 'Descrição',
                    },
                    observations: {
                      text: 'Observações',
                    },
                    save: {
                      text: 'Salvar',
                    },
                    cancel: {
                      text: 'Cancelar',
                    },
                    filename: {
                      text: 'Nome do Arquivo',
                    },
                  },
                },
                table: {
                  column: {
                    name: {
                      text: 'Nome',
                    },
                    filename: {
                      text: 'Nome do Arquivo',
                    },
                    description: {
                      text: 'Descrição',
                    },
                    actions: {
                      text: 'Ações',
                    },
                  },
                },
              },
            },
          },
        },
        sales: {
          title: {
            text: 'Análise Comercial',
          },
          tabs: {
            summary: {
              title: 'Resumo',
            },
            ticket: {
              title: 'Ticket Breakdown',
            },
            product: {
              title: 'Portfólio de Produtos',
            },
            team: {
              title: 'Equipe de Vendas',
            },
          },
        },
        funding: {
          title: {
            'total-shares': 'Total de Cotas',
            'funding-and-ir': 'Captação de Recursos & IR',
          },
          tabs: {
            captable: {
              title: 'Captable',
              table: {
                title: 'Cap table',
                total: {
                  title: 'Total de Cotas',
                },
              },
              header: {
                'create-btn': {
                  text: 'Criar',
                },
                'no-data': {
                  text: 'Nenhum dado',
                },
                period: {
                  label: 'Período',
                  'last-12': 'Últimos 12 meses',
                  'last-24': 'Últimos 24 meses',
                  'last-36': 'Últimos 36 meses',
                  'last-60': 'Últimos 5 anos',
                  all: 'Todos os dados',
                },
              },
              actions: {
                delete: {
                  text: 'Deletar',
                },
                edit: {
                  text: 'Editar',
                },
              },
              messages: {
                'save-error': 'Não foi possível salvar o registro do captable.',
              },
              form: {
                'create-title': {
                  text: 'Adicionar Sócio ou Acionista',
                },
                'edit-title': {
                  text: 'Editar Captable',
                },
                fields: {
                  'reference-date': {
                    placeholder: 'Data de Referência',
                    'required-message': 'Data de Referência é obrigatória',
                  },
                  share: {
                    placeholder: 'Quantidade de Quotas ou Ações',
                    'required-message': 'Participação é obrigatória',
                    min: 'Participação deve ser maior que 0',
                  },
                  name: {
                    placeholder: 'Nome',
                    'required-message': 'Nome é obrigatório',
                  },
                },
                save: {
                  text: 'Salvar',
                },
                cancel: {
                  text: 'Cancelar',
                },

                delete: {
                  title: 'Confirmar Remoção?',
                  cancel: 'CANCELAR',
                  confirm: 'APAGAR',
                },
              },
            },
          },
        },
      },
      'company-area': {
        title: 'Área da Empresa',
        description: 'Configure dashboards e áreas de operação da empresa',
        tabs: {
          summary: {
            title: 'Operações',
          },
          client: {
            title: 'Comercial',
          },
          analysis: {
            title: 'Análise',
          },
        },
      },
      auth: {
        'sign-in': {
          needsEmailVerification: {
            text: 'Você precisa verificar o email',
          },
          email: {
            label: 'Email',
          },
          password: {
            label: 'Senha',
          },
          'login-btn': {
            text: 'Entrar',
          },
          'forgot-password-btn': {
            text: 'Esqueci a Senha',
          },
          'first-access-btn': {
            text: 'Primeiro Acesso',
          },
          'remember-me-checkbox': {
            text: 'Lembrar',
          },
          'wrong-credentials': {
            text: 'E-mail ou senha inválidos',
          },
        },
        'sign-up': {
          needsEmailVerification: {
            text: 'Você precisa verificar o email',
          },
          'first-name': {
            label: 'Nome',
          },
          'last-name': {
            label: 'Sobrenome',
          },
          email: {
            label: 'Email',
          },
          password: {
            label: 'Senha',
          },
          'confirm-password': {
            label: 'Confirmar Senha',
          },
          'sign-up-btn': {
            text: 'Cadastrar',
          },
          'go-back-btn': {
            text: 'Voltar',
          },
          'password-mismatch': {
            text: 'As senhas não coincidem',
          },
          'email-already-registered': {
            text: 'Email já cadastrado',
            prefix: 'Faça login',
            link: 'aqui',
          },
        },
        'change-password': {
          message: {
            title: 'Redefenir Senha',
            text: 'Escolha sua nova senha abaixo',
          },
          password: {
            label: 'Senha',
          },
          confirmPassword: {
            label: 'Confirmar Senha',
          },
          'confirm-btn': {
            text: 'Confirmar',
          },
        },
        'forgot-password': {
          message: {
            title: 'Esqueceu sua senha?',
            text: 'Informe seu endereço de email e enviaremos um link para redefinir sua senha',
          },
          email: {
            label: 'Email',
          },
          'email-sent-alert': {
            text: 'Se este e-mail estiver cadastrado, um link para definir sua senha foi enviado',
          },
          'send-link-btn': {
            text: 'Enviar Link',
          },
          'sign-in-btn': {
            text: 'Fazer Login',
          },
          'password-mismatch-alert': {
            text: 'Senhas não são iguais',
          },
        },
        'first-access': {
          message: {
            title: 'Primeiro Acesso?',
            text: 'Informe seu endereço de email e enviaremos um link para redefinir sua senha',
          },
          email: {
            label: 'Email',
          },
          'email-sent-alert': {
            text: 'Se este e-mail estiver cadastrado, um link para definir sua senha foi enviado',
          },
          'send-link-btn': {
            text: 'Enviar Link',
          },
          'sign-in-btn': {
            text: 'Fazer Login',
          },
          'password-mismatch-alert': {
            text: 'Senhas não são iguais',
          },
        },
      },
      investorInvite: {
        title: 'Convite de {0}',
        description: 'Aceite este convite para acompanhar os dados de {0} como investidor.',
        accept: 'Aceitar convite',
        needsLogin: {
          text: 'Entre ou crie uma conta para aceitar este convite.',
          signIn: 'Entrar',
          signUp: 'Criar conta',
        },
        needsClient: {
          text: 'Você precisa ter sua própria empresa cadastrada na plataforma antes de aceitar um convite de investidor.',
          createClient: 'Cadastrar minha empresa',
        },
        selectClient: {
          label: 'Aceitar como',
        },
        grantee: '{0} vai acompanhar {1} no Portfólio.',
        personal: {
          description: 'Aceite este convite para acompanhar {0} no Portfólio. Vamos criar a sua conta pessoal — sem precisar cadastrar empresa.',
          nameLabel: 'Seu nome',
          nameHint: 'É o nome da sua conta pessoal.',
          reuseAccount: 'Este convite será adicionado à conta pessoal que você já tem.',
          modulesNotice: 'A empresa libera o Dataroom depois do aceite, então algumas áreas podem ficar indisponíveis no início.',
        },
        existingCompany: {
          text: '{0} já está no seu portfolio.',
          link: 'Vincular ao cadastro existente',
          createNew: 'Criar um novo cadastro',
        },
        errors: {
          not_found: 'Este link de convite é inválido.',
          expired: 'Este convite expirou.',
          revoked: 'Este convite foi revogado.',
          already_accepted: 'Este convite já foi aceito.',
          refused: 'Você recusou este convite.',
          declineFailed: 'Não foi possível recusar o convite. Tente novamente.',
          wrong_email: 'Este convite foi enviado para outro e-mail. Entre com o e-mail convidado para aceitá-lo.',
          acceptFailed: 'Não foi possível aceitar o convite. Tente novamente.',
        },
        switchAccount: 'Entrar com outra conta',
        decline: 'Recusar',
      },
      configuration: {
        title: 'Configuração',
        dataroom: {
          moduleConfigTitle: 'Visual',
          // Index.vue labels and messages
          logoFull: 'Logo Completo',
          insertPhotoUrl: 'Inserir URL da foto',
          uploadImage: 'Upload de imagem',
          logoShort: 'Logo Curta',
          colors: 'Cores',
          primaryColor: 'Cor Primária',
          secondaryColor: 'Cor Secundária',
          selectPrimaryColor: 'Selecionar cor primária',
          selectSecondaryColor: 'Selecionar cor secundária',
          confirm: 'Confirmar',
          clientInfo: 'Informações do Cliente',
          address: 'Endereço',
          website: 'Website',
          features: 'Funcionalidades',
          enableDataroom: 'Habilitar Dataroom',
          enableCaptable: 'Habilitar Captable',
          enableFinancialModule: 'Habilitar Dados Financeiros',
          enableAccountingModule: 'Habilitar Dados Contábeis',
          enableIndicatorsModule: 'Habilitar Dados Complementares',
          enableCompanyAreaModule: 'Habilitar Área da Empresa',
          dashboard: 'Home do Dataroom',
          changesSaved: 'Alterações salvas com sucesso',
          configurationSaved: 'Configuração salva com sucesso',
          configurationSaveError: 'Erro ao salvar configuração',
          defaultDashboard: 'Home do Cockpit',
          dashboardDataroom: 'Home Customizada',
          invalidFileType: 'Selecione apenas arquivos de imagem',
          urlGenerationError: 'Erro ao gerar URL da imagem',
          embedLinks: 'Incorporar vídeo ou outros links',
          companyEmbedUrl: 'Link de Incorporação',
          embedUrlHelp:
            'Cole o link de um vídeo ou post público do YouTube, Instagram, LinkedIn, Vimeo, Google Drive ou Loom. Pode ser o link de compartilhamento — ele é convertido para o formato de incorporação automaticamente.',
          embedPreview: 'Pré-visualização',
          mediaModeLink: 'Link',
          mediaModeUpload: 'Enviar vídeo',
          videoUpload: 'Vídeo da empresa',
          videoUploadHelp: 'MP4 (H.264), WebM ou MOV de até 500 MB. Ele toca na home do dataroom com player próprio.',
          noVideo: 'Nenhum vídeo enviado',
          selectVideo: 'Selecionar vídeo',
          replaceVideo: 'Trocar vídeo',
          removeVideo: 'Remover vídeo',
          validatingVideo: 'Verificando vídeo…',
          uploadingVideo: 'Enviando vídeo',
          // Dialogs and common actions
          editDocument: 'Editar Documento',
          addDocument: 'Adicionar Documento',
          folder: 'Pasta',
          newFolder: 'Nova Pasta',
          createNewFolder: 'Criar Nova Pasta',
          enterNewFolderName: 'Digite o nome da nova pasta',
          documentName: 'Nome do Documento',
          documentLink: 'Link do Documento',
          uploadFile: 'Upload de Arquivo',
          uploadedFile: 'Arquivo carregado',
          externalLink: 'Link externo',
          cancel: 'Cancelar',
          save: 'Salvar',
          edit: 'Editar',

          team: 'Equipe',
          noMembersFound: 'Nenhum membro encontrado',
          name: 'Nome',
          position: 'Cargo',
          description: 'Descrição',
          addMember: 'Adicionar membro',

          documents: 'Documentos',
          searchDocuments: 'Pesquisar documentos',
          documentNameHeader: 'Documento',
          folderHeader: 'Pasta',
          lastModified: 'Última modificação',
          noDocuments: 'Nenhum documento',
          noFolder: '—',
          addDocumentBtn: 'Adicionar Documento',
          allFolders: 'Todas as pastas',
          addDocumentToFolder: 'Adicionar Documento à Pasta',
          selectFolder: 'Selecionar Pasta',

          confirmDelete: 'Confirmar exclusão',
          deleteDocumentConfirm: 'Deseja realmente excluir o documento',
          delete: 'Deletar',
          confirmRemoveTeamMember: 'Deseja realmente remover o membro da equipe?',

          linkedinEmbedUrl: 'Link de Incorporação do Linkedin',
          linkedinEmbedUrlHelp:
            'Cole aqui o código de incorporação completo. Normalmente é um trecho de código iframe que você copia e cola como está. Exemplo: <iframe src="https://www.linkedin.com/embed/feed/update/urn:li:activity:123456789"></iframe>',
          linkedinFeed: 'Feed do LinkedIn',

          businessPlanPitchDeck: 'Plano de Negócios / Pitch Deck / Roadmap',
          businessPlan: 'Plano de Negócios',
          fileUploaded: 'Arquivo carregado',

          errors: {
            invalidUrl: 'URL inválida',
            unsupportedVideoUrl: 'Link não suportado. Use um vídeo ou post público do YouTube, Instagram, LinkedIn, Vimeo, Google Drive ou Loom',
            videoType: 'Formato não suportado. Use MP4, WebM ou MOV',
            videoSize: 'O vídeo tem mais de 500 MB',
            videoUnplayable: 'O navegador não consegue reproduzir este vídeo. Exporte em MP4 (H.264) e tente de novo',
            videoUpload: 'Erro ao enviar o vídeo',
            urlMustStartWith: 'URL deve começar com http:// ou https://',
            pleaseEnterValidUrl: 'Por favor, insira uma URL válida',
            invalidInstagramHandle: 'Instagram inválido',
            invalidLinkedinHandle: 'LinkedIn inválido',
          },

          fileTooLarge: 'O arquivo é muito grande',
          urlMustStartWith: 'URL deve começar com http:// ou https://',
          uploadError: 'Erro ao carregar arquivo',
          missingDocumentFields: 'Preencha a pasta, nome e arquivo ou link',
          documentPreparedToSave: 'Documento preparado para salvar',
          memberRemoved: 'Membro removido',
          businessPlanRemoved: 'Plano de Negócios removido',
          folderRemoved: 'Pasta removida',
        },
        financial: {
          'create-config-btn': {
            text: 'Adicionar Novo Provedor',
          },
          form: {
            title: 'Informe os dados do seu provedor financeiro',
            'title-filled': 'Dados do Provedor',
            text: 'Vamos solicitar os dados para implementação do módulo financeiro!',
            'text-filled': 'Veja ou edite os dados do provedor cadastrado para o módulo financeiro.',
            provider: 'Provedor',
            'provider-label': 'Selecione um provedor',
            hide: 'Ocultar',
            show: 'Mostrar',
            save: 'Salvar',
            edit: 'Editar',
            cancel: 'Cancelar',
            disable: 'Remover Provedor',
            'disable-title': 'Remover provedor?',
            'disable-confirm': 'Isso removerá o provedor atual para este módulo. Deseja continuar?',
            'disable-error': 'Erro ao remover provedor.',
            'disable-success': 'Provedor removido com sucesso.',
            'no-changes-warning': 'Não há alterações para salvar',
            name: 'Provedor',
            status: 'Status',
            actions: 'Ações',
            'conta-azul-authenticated': 'Conta Azul está autenticada',
            'conta-azul': {
              step1: 'Siga o passo a passo abaixo',
              step2: 'Na criação do parceiro, utilize os seguintes dados:',
              step3: 'Após a criação do parceiro, clique no botão "Salvar" para finalizar a configuração.',
              responsible: 'Nome do Responsável',
              company: 'Nome da Empresa',
              cnpj: 'CNPJ',
              email: 'E-mail',
              phone: 'Telefone',
              service: 'Serviço Prestado',
              authorize: 'Autorizar com Conta Azul',
              'authorize-text': 'Clique para autorizar a integração com sua conta do Conta Azul',
              'authorize-success': 'Autorização concluída com sucesso! O código foi preenchido automaticamente.',
            },
          },
          'configuration-in-process': {
            title: 'Configuração em andamento',
            text: 'A configuração do seu módulo financeiro está em andamento. Você será notificado assim que a configuração for concluída.',
            button: 'Voltar para Configuração',
          },
          tabs: {
            providers: 'Configuração de Provedores',
            'chart-accounts': 'Plano de Contas Personalizado',
            'expected-chart-accounts': 'Plano de Contas Esperado',
            'account-filter-settings': 'Configurações de filtro de contas',
          },
          'account-filter-settings': {
            empty: 'Nenhuma conta bancária encontrada para este cliente.',
            'toggle-visible': 'Exibir no filtro de contas do fluxo de caixa',
            'toggle-default': 'Conta padrão para lançamentos sem conta bancária',
            'default-badge': 'Padrão',
            'default-changed': 'Conta padrão atualizada — a anterior foi desmarcada automaticamente.',
            save: 'Salvar',
            discard: 'Descartar',
            'update-error': 'Não foi possível atualizar o filtro da conta.',
            'update-success': 'Filtro de contas atualizado com sucesso.',
            'update-success-on': 'Conta ativada no filtro com sucesso.',
            'update-success-off': 'Conta desativada no filtro com sucesso.',
          },
          'custom-chart-accounts': {
            disabled: 'Contas desabilitadas',
            disable: 'Desabilitar conta',
            enable: 'Habilitar',
            'revert-disable': 'Reverter desabilitação',
            'disable-title': 'Desabilitar Conta',
            'disable-confirm': 'Tem certeza que deseja desabilitar esta conta?',
            'disable-success': 'Conta desabilitada com sucesso',
            'disable-error': 'Erro ao desabilitar conta',
            'no-active-config': 'Nenhuma configuração ativa encontrada',
            'disable-button': 'Desabilitar',
            'configurations-to-remove': 'Configurações que serão removidas:',
            'disable-message': 'Desabilitando esta conta, as seguintes configurações serão removidas:',
            'duplicate-config-error':
              'A configuração de destino já existe para esta conta, exclua ou edite a configuração existente para prosseguir',
            'new-configuration': 'Nova configuração',
            'new-config-title': 'Criar uma configuração adicional para esta conta',
            delete: 'Excluir',
            'delete-title': 'Excluir configuração',
            'confirm-delete': 'Tem certeza que deseja excluir esta configuração?',
            'delete-success': 'Configuração excluída com sucesso',
            'delete-error': 'Erro ao excluir configuração',
            title: 'Plano de Contas Personalizado',
            description: 'Visualização das contas personalizadas configuradas por categoria',
            search: 'Pesquisar contas...',
            'account-name': 'Nome da Conta',
            identifier: 'Identificador',
            'category-name': 'Nome da Categoria',
            'category-identifier': 'Identificador da Categoria',
            multiplier: 'Multiplicador',
            visible: 'Visível',
            configured: 'Contas configuradas',
            unconfigured: 'Contas não configuradas',
            'no-data': 'Nenhuma conta personalizada encontrada para esta configuração',
            'no-search-results': 'Nenhum resultado encontrado para a pesquisa',
            'try-different-search': 'Tente uma pesquisa diferente ou limpe o filtro',
            'select-config': 'Selecione uma configuração para visualizar as contas',
            'no-configurations': 'Nenhuma configuração disponível',
            category: 'Categoria',
            edit: 'Editar',
            save: 'Salvar',
            cancel: 'Cancelar',
            approved: 'Validado',
            'save-success': 'Contas atualizadas com sucesso!',
            'save-partial': 'Algumas contas atualizadas, outras com erro.',
            'save-error': 'Erro ao salvar alterações',
            'no-changes': 'Nenhuma alteração para salvar',
            'approval-required-tooltip': 'Esta conta precisa ser aprovada. Confirme a aprovação no modo de edição.',
            'confirm-title': 'Confirmar Alterações',
            'no-changes-message': 'Nenhuma alteração para salvar',
            'clone-title': 'Clonar configuração para filhos',
            'clone-message': 'A configuração do pai será aplicada aos seguintes filhos elegíveis:',
            'clone-confirm': 'Clonar para filhos',
            'clone-success': 'Configuração clonada para filhos com sucesso',
            'clone-error': 'Erro ao clonar configuração',
            'all-children-configured': 'Todos os filhos já estão configurados',
            'no-eligible-children-clone': 'Nenhum filho elegível para clonar',
            'cascade-disable-title': 'Desabilitar pai e filhos',
            'cascade-disable-message': 'Desabilitar este pai também desabilitará os seguintes filhos configurados:',
            'cascade-disable-success': 'Pai e filhos desabilitados com sucesso',
            'ignore-cost-center': {
              label: 'Considerar centros de custo',
              tooltip: 'Desligado: todos os centros de custo de uma conta usam o mesmo de-para, e novas combinações de conta + centro de custo vindas das sincronizações já chegam configuradas. Ligue para configurar cada centro de custo separadamente.',
              'enable-title': 'Desconsiderar centros de custo',
              'enable-message': 'Todos os centros de custo de uma conta passarão a compartilhar o mesmo de-para. Combinações de conta + centro de custo não configuradas recebem agora o de-para da conta, e as novas que vierem em sincronizações futuras serão configuradas automaticamente. Editar o de-para de um centro de custo atualiza todos os centros de custo daquela conta.',
              'enable-note': 'Combinações que já estão configuradas de forma diferente são mantidas até que uma delas seja editada.',
              'enable-confirm': 'Desconsiderar centros de custo',
              'enable-success': 'Centros de custo desconsiderados no de-para',
              'disable-title': 'Voltar a considerar centros de custo',
              'disable-message': 'Os de-paras atuais são mantidos e cada centro de custo volta a poder ser editado separadamente. Novas combinações vindas de sincronizações futuras voltarão a aparecer como não configuradas.',
              'disable-confirm': 'Considerar centros de custo',
              'disable-success': 'Centros de custo voltaram a ser considerados no de-para',
              error: 'Não foi possível salvar a configuração',
            },
            'collapse-all': 'Recolher todos',
            'expand-all': 'Expandir todos',
            'group-status-configured': 'Todas as contas configuradas',
            'group-status-partial': 'Algumas contas não configuradas',
            'group-status-unconfigured': 'Nenhuma conta configurada',
            'pending-configurations-tooltip': 'Configurações pendentes',
            'refresh-omie': 'Atualizar omie',
            'refresh-omie-in-progress': 'Atualizando omie…',
            'refresh-sienge': 'Atualizar Sienge',
            'refresh-sienge-in-progress': 'Atualizando Sienge…',
          },
          'expected-chart-accounts': {
            title: 'Plano de Contas Esperado',
            description: 'Gerencie os registros de contas esperadas do plano de contas personalizado.',
            create: 'Novo',
            'create-title': 'Novo registro',
            'edit-title': 'Editar',
            format: 'Formato',
            name: 'Nome',
            'description-field': 'Observação',
            identifier: 'Identificador',
            'raw-value': 'Valor bruto',
            'reference-date': 'Data referência',
            'numeric-value': 'Valor numérico',
            actions: 'Ações',
            'no-data': 'Nenhum registro encontrado.',
            cancel: 'Cancelar',
            save: 'Salvar',
            edit: 'Editar',
            'delete-title': 'Excluir registro',
            'delete-message': 'Tem certeza que deseja excluir este registro? Esta ação pode ser revertida posteriormente.',
            delete: 'Excluir',
            required: 'Campo obrigatório',
            'numeric-required': 'Valor numérico obrigatório',
            'save-success': 'Registro atualizado com sucesso.',
            'create-success': 'Registro criado com sucesso.',
            'save-error': 'Erro ao salvar.',
            'delete-success': 'Registro excluído com sucesso.',
            'delete-error': 'Erro ao excluir.',
            'cost-center': 'Centro de custo',
            category: 'Categoria',
            back: 'Cenários',
            'scenario-default': 'Padrão',
            'scenario-default-description': 'Registros sem cenário associado',
            'scenario-new': 'Novo Cenário',
            'scenario-create-title': 'Novo cenário',
            'scenario-edit-title': 'Editar cenário',
            'scenario-name': 'Nome',
            'scenario-description': 'Observação',
            'scenario-type': 'Tipo',
            'scenario-type-de': 'De',
            'scenario-type-para': 'Para',
            'scenario-type-cost-center': 'Centro de custo',
            'scenario-configuration': 'Configuração',
            'scenario-save-success': 'Cenário atualizado com sucesso.',
            'scenario-create-success': 'Cenário criado com sucesso.',
            'scenario-save-error': 'Erro ao salvar cenário.',
            'scenario-delete-title': 'Excluir cenário',
            'scenario-delete-message': 'Tem certeza que deseja excluir este cenário? Esta ação não poderá ser realizada se existirem registros associados.',
            'scenario-delete-success': 'Cenário excluído com sucesso.',
            'scenario-delete-error': 'Erro ao excluir cenário.',
            'scenario-no-data': 'Nenhum cenário encontrado.',
            search: 'Pesquisar',
            'no-search-results': 'Nenhum resultado corresponde à pesquisa.',
            'detail-description': 'Registros de contas previstas para este cenário',
            'month-from': 'Mês inicial',
            'month-to': 'Mês final',
            'bulk-delete': 'Excluir em lote',
            'bulk-delete-title': 'Excluir registros em lote',
            'bulk-delete-message': 'Tem certeza que deseja excluir os registros selecionados?',
            'bulk-delete-all-message': 'Tem certeza que deseja excluir todos os registros deste cenário?',
            'bulk-delete-count': 'Excluir selecionados',
            'bulk-delete-all-count': 'Excluir todos',
            'bulk-delete-success': 'Registros excluídos com sucesso.',
            'bulk-delete-error': 'Erro ao excluir registros em lote.',
            'bulk-page-selected': 'registros desta página selecionados.',
            'bulk-select-all': 'Selecionar todos os registros',
            'bulk-all-selected': 'Todos os registros serão excluídos.',
            'bulk-deselect': 'Desmarcar',
          },
          transactions: {
            title: 'Transações Detalhadas',
            description: 'Visualize todas as transações financeiras',
            'button-text': 'Ver Transações Detalhadas',
            'search-placeholder': 'Pesquisar transações...',
            'date-from': 'Data Inicial',
            'date-to': 'Data Final',
            'reference-date': 'Data',
            name: 'Nome',
            'numeric-value': 'Valor',
            'supplier-name': 'Nome do Fornecedor',
            'category-name': 'Nome da Categoria',
            'category-identifier': 'Identificador da Categoria',
            'original-category': 'Categoria Original',
            'original-category-name': 'Nome Categoria Original',
            'custom-category': 'Categoria Personalizada',
            'custom-category-name': 'Nome Categoria Personalizada',
            actions: 'Ações',
            'no-data': 'Nenhuma transação encontrada',
            loading: 'Carregando transações...',
            'custom-category-indicator': '= Categoria Personalizada',
            'show-only-custom-category': 'Mostrar apenas Categoria Personalizada',
            'custom-rules': {
              title: 'Regras de Categoria Personalizadas',
              add: 'Adicionar Regra',
              edit: 'Editar Regra',
              remove: 'Remover Regra',
              'remove-confirm': 'Tem certeza que deseja remover esta regra personalizada?',
              'original-category': 'Categoria Original',
              'custom-category': 'Categoria Personalizada',
              'category-name': 'Nome da Categoria',
              'supplier-name': 'Nome do Fornecedor',
              'category-identifier': 'Identificador da Categoria',
              'identifier-tooltip': 'O identificador não pode ser igual a uma categoria existente',
              selected: 'Categoria Selecionada',
              save: 'Salvar',
              cancel: 'Cancelar',
              required: 'Campo obrigatório',
              'identifier-exists': 'Este identificador já existe nas categorias configuradas',
              'category-name-exists': 'Já existe uma categoria com este nome',
              'save-success': 'Regra personalizada salva com sucesso',
              'save-error': 'Erro ao salvar regra personalizada',
              'delete-success': 'Regra personalizada removida com sucesso',
              'delete-error': 'Erro ao remover regra personalizada',
            },
          },
        },
        indicators: {
          tabs: {
            'kpi-table-configuration': {
              title: 'Tabelas',
              header: {
                'create-btn': {
                  text: 'Nova Tabela de Dados',
                },
                'search-input': {
                  placeholder: 'Procurar por nome...',
                },
                'order-tables-btn': {
                  text: 'Ordem das Tabelas',
                  dialog: {
                    text: 'Definir Ordem das Tabelas',
                    save: 'Salvar',
                    cancel: 'Cancelar',
                  },
                },
              },
              form: {
                'create-title': {
                  text: 'Nova Tabela de Dados',
                },
                'edit-title': {
                  text: 'Editar Tabela de Dados',
                },
                fields: {
                  name: {
                    placeholder: 'Nome',
                    'required-message': 'Nome é obrigatório',
                  },
                  indicator: {
                    placeholder: 'Selecione os Dados',
                  },
                  placeholder: 'Indicator',
                },
                save: {
                  text: 'Salvar',
                },
                cancel: {
                  text: 'Cancelar',
                },
              },
              delete: {
                'confirmation-text': 'Confirmar Remoção?',
                text: 'Apagar',
              },
              table: {
                column: {
                  name: {
                    text: 'Tabela',
                  },
                  format: {
                    text: 'Tipo',
                  },
                  actions: {
                    text: 'Ações',
                  },
                },
              },
            },
            'kpi-configuration': {
              title: 'Dados Cadastrados',
              header: {
                'create-btn': {
                  text: 'Cadastrar Novos Dados',
                },
                'search-input': {
                  placeholder: 'Procurar por nome...',
                },
              },
              form: {
                'create-title': {
                  text: 'Cadastro de Novo Dado',
                },
                'edit-title': {
                  text: 'Editar Dados',
                },
                fields: {
                  name: {
                    placeholder: 'Nome',
                    'required-message': 'Nome é obrigatório',
                  },
                  format: {
                    'required-message': 'Tipo é obrigatório',
                    placeholder: 'Tipo',
                  },
                },
                save: {
                  text: 'Salvar',
                },
                cancel: {
                  text: 'Cancelar',
                },
                delete: {
                  text: 'Deletar',
                  confirmation: {
                    title: 'Confirmar Remoção?',
                    confirm: 'APAGAR',
                    cancel: 'CANCELAR',
                  },
                },
              },
              table: {
                column: {
                  name: {
                    text: 'Dados Cadastrados',
                  },
                  format: {
                    text: 'Tipo',
                  },
                  actions: {
                    text: 'Ações',
                  },
                },
              },
            },
            'expected-scenarios': {
              'tab': 'Cenários',
              'title': 'Cenários dos Dados Complementares',
              'description': 'Cadastre cenários de orçamento, meta ou projeção para os seus dados complementares e preencha-os por planilha',
              'search': 'Pesquisar cenários',
              'new': 'Novo Cenário',
              'name': 'Nome',
              'observation': 'Observação',
              'values-count': 'Valores cadastrados',
              'actions': 'Ações',
              'edit': 'Editar',
              'delete': 'Excluir',
              'no-data': 'Nenhum cenário cadastrado ainda.',
              'no-search-results': 'Nenhum cenário corresponde à pesquisa.',
              'create-title': 'Novo cenário',
              'edit-title': 'Editar cenário',
              'save': 'Salvar',
              'cancel': 'Cancelar',
              'required': 'Campo obrigatório',
              'duplicate-name': 'Já existe um cenário com esse nome',
              'save-success': 'Cenário salvo',
              'save-error': 'Erro ao salvar o cenário',
              'delete-title': 'Excluir cenário',
              'delete-message': 'Tem certeza que deseja excluir este cenário? Ele só pode ser excluído quando não tiver valores.',
              'delete-blocked': 'Este cenário ainda tem valores. Limpe-os pela planilha antes de excluí-lo.',
              'delete-success': 'Cenário excluído',
              'delete-error': 'Erro ao excluir o cenário',
              'detail-description': 'Valores previstos deste cenário',
              'detail-empty': 'Nenhum valor ainda. Baixe o modelo, preencha e faça o upload.',
              'indicator': 'Dado complementar',
              'type': 'Tipo',
              'download-template': 'Baixar modelo',
              'download': 'Baixar',
              'download-error': 'Erro ao gerar o modelo',
              'start-month': 'Mês inicial',
              'end-month': 'Mês final',
              'indicators': 'Dados complementares',
              'indicators-hint': 'Deixe vazio para incluir todos os dados complementares numéricos',
              'upload': 'Upload de planilha',
              'file': 'Selecione o arquivo XLSX',
              'upload-error': 'Erro ao processar a planilha',
              'upload-success': 'Planilha aplicada',
              'preview-title': 'Preview da planilha',
              'preview-no-changes': 'A planilha não altera nenhum valor.',
              'confirm': 'Confirmar',
              'summary-created': 'Novos',
              'summary-updated': 'Alterados',
              'summary-deleted': 'Removidos',
              'invalid-title': 'A planilha não pôde ser importada',
              'invalid-cells': 'Estas células não são números válidos:',
              'template-invalid': 'A planilha não segue o modelo:',
              'ok': 'OK',
            },
          },
        },
        accounting: {
          title: 'Contábil',
          form: {
            'accounting-title': 'Informe os dados do seu contador',
            'accounting-title-edit': 'Editar Dados do Contador',
            'accounting-title-filled': 'Dados do seu Contador',
            'accounting-text': 'Vamos solicitar os dados para implementação do módulo contábil!',
            'accounting-text-filled': 'Veja ou edite os dados do contador cadastrados para o módulo contábil.',
            'accounting-cnpj': 'CNPJ do Escritório Contábil',
            'accountant-email': 'E-mail do Contador',
            'company-cnpj': 'Confirme o CNPJ da sua empresa*',
            'accountant-phone': 'Telefone do Contador',
            'cnpj-error-incomplete': 'CNPJ incompleto',
            'cnpj-error-more-digits': 'CNPJ com dígitos a mais',
            'email-error-invalid': 'E-mail inválido',
            'phone-error-incomplete': 'Telefone incompleto',
            'phone-error-more-digits': 'Telefone com dígitos a mais',
            'form-error-invalid': 'Por favor, preencha todos os campos obrigatórios corretamente.',
            'save-success': 'Configuração salva com sucesso!',
            'save-error': 'Erro ao salvar configuração!',
            'add-email': 'Adicionar Email',
            edit: 'Editar',
            cancel: 'Cancelar',
            save: 'Salvar',
          },
          'configuration-in-process': {
            title: 'Configuração em andamento',
            text: 'A configuração do seu módulo contábil está em andamento. Você será notificado assim que a configuração for concluída.',
            button: 'Voltar para Configuração',
          },
        },
        tabs: {
          modules: {
            title: 'Módulos',
            cards: {
              accounting: {
                title: 'Contábil',
                description: 'Confira o Balanço Patrimonial, DRE e outras demonstrações contábeis da empresa',
              },
              financial: {
                title: 'Financeiro',
                description:
                  'Faça a análise de Fluxo de Caixa, DRE Gerencial, Burn Rate, Runway e outras métricas financeiras',
              },
              sales: {
                title: 'Comercial',
                description:
                  'Confira as métricas comerciais da empresa. Pipeline completo, Taxa de conversão, Churn Rate e muito mais',
              },
              indicators: {
                title: 'Dados Complementares',
                description: 'Controle as métricas e dados complementares das atividades operacionais da sua empresa',
              },
              dataroom: {
                title: 'Dataroom',
                description: 'Centralize e compartilhe documentos importantes da empresa com segurança',
              },
              webhooks: {
                title: 'Webhooks',
                description: 'Gerencie assinaturas de webhook para receber notificações em tempo real',
              },
              'api-keys': {
                title: 'Chaves de API',
                description: 'Crie e gerencie chaves de API para acesso programático aos seus dados',
              },
            },
          },
          roles: {
            title: 'Perfis de Acesso',
            list: {
              title: 'Lista',
            },
            filter: {
              title: 'Filtrar',
            },
            create: {
              title: 'Criar Novo Perfil de Acesso',
            },
            edit: {
              title: 'Editar Perfil de Acesso',
            },
            delete: {
              title: 'Apagar Perfil de Acesso',
            },
            name: {
              title: 'Nome',
            },
            description: {
              title: 'Descrição',
            },
            actions: {
              title: 'Ações',
            },
            permissions: {
              title: 'Permissões',
              management: 'Gerenciar Permissões',
              selectAllRead: 'Selecionar Permissões de Leitura',
              unselectAllRead: 'Desmarcar Permissões de Leitura',
              selectAllEdit: 'Selecionar Permissões de Edição',
              unselectAllEdit: 'Desmarcar Permissões de Edição',
            },
            role: {
              title: 'Perfil',
            },
            save: {
              title: 'Salvar',
              success: 'Perfis de acesso atualizados com sucesso',
            },
            cancel: {
              title: 'Cancelar',
            },
            delete_confirmation: {
              title: 'Tem certeza que deseja excluir este perfil?',
            },
          },

          alerts: {
            title: 'Alertas',
          },
          users: {
            title: 'Usuários',
            tabs: {
              'users-table': {
                header: {
                  'search-input': {
                    placeholder: 'Pesquisar por nome ou email',
                  },
                  'invite-btn': {
                    text: 'Convidar Usuário',
                  },
                },
                table: {
                  column: {
                    'display-name': {
                      text: 'Nome',
                    },
                    email: {
                      text: 'E-mail',
                    },

                    'client-role': {
                      text: 'Perfil de Acesso',
                    },
                    actions: {
                      text: 'Ações',
                    },
                  },
                },
              },
            },
            invite: {
              title: 'Convidar Usuário',
              name: {
                placeholder: 'Nome',
                'required-message': 'Nome é obrigatório',
              },
              role: {
                placeholder: 'Perfil de Acesso',
                'required-message': 'Perfil de Acesso é obrigatório',
              },
              email: {
                placeholder: 'E-mail',
                'required-message': 'E-mail é obrigatório',
                'invalid-message': 'E-mail inválido',
                'already-exists-message': 'Este e-mail já pertence a um usuário existente',
              },
              send: {
                text: 'Enviar',
              },
              cancel: {
                text: 'Cancelar',
              },
              success: {
                text: 'Usuário convidado com sucesso',
              },
              error: {
                text: 'Erro ao convidar usuário',
              },
            },
            'pending-invites': {
              title: 'Convites Pendentes',
              cancel: {
                title: 'Cancelar Convite',
                confirmation: {
                  title: 'Tem certeza que deseja cancelar este convite?',
                },
                cancel: {
                  text: 'Voltar',
                },
              },
              'cancel-success': {
                text: 'Convite cancelado com sucesso',
              },
              'cancel-error': {
                text: 'Erro ao cancelar convite',
              },
              table: {
                column: {
                  email: {
                    text: 'E-mail',
                  },
                  actions: {
                    text: 'Ações',
                  },
                },
              },
            },
            remove: {
              title: 'Remover Usuário',
              confirmation: {
                title: 'Remover usuário?',
              },
              message: 'Está operação não pode ser revertida.',
            },
            edit_role: {
              title: 'Editar Perfil de Acesso',
              select_role: 'Selecione um perfil de acesso',
            },
            cancel: {
              title: 'Cancelar',
            },
            save: {
              title: 'Salvar',
            },
          },
          'company-area': {
            title: 'Área da Empresa',
            description: 'Configure dashboards para a área da empresa',
            'enable-company-area': 'Habilitar Área da Empresa',
            messages: {
              'config-saved': 'Configuração salva com sucesso',
              'config-error': 'Erro ao salvar configuração',
            },
            'dashboard-type': {
              title: 'Dashboard Type',
              label: 'Dashboard Type',
              select: 'Select dashboard type',
              'selected-type': 'Selected type: {type}',
              'no-type-selected': 'None',
            },
            buttons: {
              edit: 'Editar',
              cancel: 'Cancelar',
              confirm: 'Confirmar',
            },
            'operation-area': {
              title: 'Área de Operação',
              select: 'Selecione a área de operação',
              'selected-area': 'Área selecionada: {area}',
              'no-area-selected': 'Nenhuma',
              options: {
                operacao: 'Operação',
                comercial: 'Comercial',
                analise: 'Análise',
              },
              description: {
                operacao: 'Configure dashboards para a área de operação',
                comercial: 'Configure dashboards para a área comercial',
                analise: 'Configure dashboards para a área de análise',
              },
            },
            'select-type': {
              title: 'Selecione um tipo de dashboard',
              description:
                'Escolha entre Metabase, Google Sheets ou URL pública para configurar os dashboards da empresa.',
            },
            dashboards: {
              title: 'Configuração de Dashboard',
              'not-configured': 'Não configurado',
              display: {
                label: 'Exibir dashboard',
                yes: 'Sim',
                no: 'Não',
              },
              tooltip: {
                'embed-url': 'A URL deve conter "/embed" para funcionar corretamente',
              },
              messages: {
                'save-success': 'Configuração salva com sucesso',
                'save-error': 'Erro ao salvar configuração',
                'test-success': 'Teste de conexão bem-sucedido',
                'test-error': 'Teste de conexão falhou',
                'remove-success': 'Configuração removida com sucesso',
                'remove-error': 'Erro ao remover configuração',
                'metabase-success': 'Dashboard Metabase configurado com sucesso!',
                'googlesheets-success': 'Dashboard Google Sheets configurado com sucesso!',
                'publicurl-success': 'Dashboard URL Pública configurado com sucesso!',
                'metabase-error': 'Erro ao salvar configuração do Metabase',
                'googlesheets-error': 'Erro ao salvar configuração do Google Sheets',
                'publicurl-error': 'Erro ao salvar configuração da URL Pública',
                'validation-error': 'Por favor, preencha todos os campos obrigatórios',
                'load-error': 'Erro ao carregar configurações existentes',
              },
              metabase: {
                title: 'Metabase',
                url: 'URL do Metabase',
                urlPlaceholder: 'https://seu-metabase.com',
                apiKey: 'Chave da API',
                apiKeyPlaceholder: 'Digite sua chave da API do Metabase',
                dashboardId: 'ID do Dashboard',
                dashboardIdPlaceholder: 'Ex: 1',
                save: 'Salvar',
              },
              googleSheets: {
                title: 'Google Sheets',
                url: 'URL do Google Sheets',
                urlPlaceholder: 'https://docs.google.com/spreadsheets/d/...',
                save: 'Salvar',
              },
              publicUrl: {
                title: 'URL Pública',
                url: 'URL Pública',
                urlPlaceholder: 'https://exemplo.com/dashboard',
                save: 'Salvar',
              },
              saved: {
                title: 'Saved Configurations',
                empty: {
                  title: 'No configurations saved yet.',
                  subtitle: 'Add dashboards above to get started.',
                },
                test: 'Test',
                remove: 'Remove',
                types: {
                  metabase: 'Metabase',
                  'google-sheets': 'Google Sheets',
                  'public-url': 'Public URL',
                },
              },
            },
          },
          webhooks: {
            title: 'Webhooks',
            description: 'Gerencie assinaturas de webhook para receber notificações em tempo real',
            'new-btn': 'Novo Webhook',
            headers: {
              name: 'Nome',
              url: 'URL',
              events: 'Eventos',
              status: 'Status',
              'created-at': 'Criado em',
              actions: 'Ações',
            },
            status: {
              active: 'Ativo',
              paused: 'Pausado',
            },
            actions: {
              test: 'Testar',
              logs: 'Log de entregas',
              edit: 'Editar',
              pause: 'Pausar',
              resume: 'Retomar',
              delete: 'Excluir',
            },
            'event-types': {
              captable_updated: 'Cap table atualizado',
              receivables_updated: 'Recebíveis atualizados',
              payables_updated: 'Contas a pagar atualizadas',
              accounting_transactions_updated: 'Transações contábeis atualizadas',
              financial_transactions_updated: 'Transações financeiras atualizadas',
            },
            form: {
              'create-title': 'Novo Webhook',
              'edit-title': 'Editar Webhook',
              name: 'Nome',
              url: 'URL',
              'event-types': 'Eventos',
              cancel: 'Cancelar',
              save: 'Salvar',
              rules: {
                'name-required': 'Nome é obrigatório',
                'url-required': 'URL é obrigatória',
                'url-https': 'A URL deve começar com https://',
                'event-types-required': 'Selecione pelo menos um evento',
              },
            },
            secret: {
              title: 'Chave de assinatura',
              warning: 'Esta é a única vez que a chave é exibida. Copie agora — ela não poderá ser recuperada novamente.',
              copy: 'Copiar',
              copied: 'Chave copiada para a área de transferência',
              done: 'Concluído',
            },
            'delete-confirmation': {
              title: 'Excluir este webhook?',
              text: 'Este webhook está ativo e deixará de receber eventos imediatamente. Essa ação pode ser revertida criando uma nova assinatura.',
            },
            'delivery-log': {
              title: 'Entregas recentes',
              close: 'Fechar',
              headers: {
                event: 'Evento',
                status: 'Status',
                attempts: 'Tentativas',
                'delivered-at': 'Entregue em',
                error: 'Erro',
              },
            },
            messages: {
              'load-error': 'Erro ao carregar webhooks',
              'create-success': 'Webhook criado com sucesso',
              'create-error': 'Erro ao criar webhook',
              'update-success': 'Webhook atualizado com sucesso',
              'update-error': 'Erro ao atualizar webhook',
              'delete-success': 'Webhook excluído com sucesso',
              'delete-error': 'Erro ao excluir webhook',
              'test-success': 'Teste entregue com sucesso (status {status})',
              'test-error': 'Falha no envio do teste: {error}',
            },
          },
          'api-keys': {
            title: 'Chaves de API',
            description: 'Crie e gerencie chaves de API para acesso programático aos seus dados',
            'new-btn': 'Nova Chave de API',
            tabs: {
              keys: 'Chaves',
              'access-requests': 'Solicitações de acesso',
            },
            headers: {
              name: 'Nome',
              prefix: 'Prefixo',
              scopes: 'Escopos',
              status: 'Status',
              'created-at': 'Criado em',
              actions: 'Ações',
            },
            status: {
              active: 'Ativa',
              revoked: 'Revogada',
            },
            actions: {
              revoke: 'Revogar',
            },
            scopes: {
              financial: 'Financeiro',
              accounting: 'Contábil',
              webhooks: 'Webhooks',
              reports: 'Relatórios',
              captable: 'Cap table',
              complementary: 'Dados complementares',
            },
            form: {
              'create-title': 'Nova Chave de API',
              name: 'Nome',
              scopes: 'Escopos',
              cancel: 'Cancelar',
              save: 'Salvar',
              rules: {
                'name-required': 'Nome é obrigatório',
                'scopes-required': 'Selecione pelo menos um escopo',
              },
            },
            token: {
              title: 'Token da chave de API',
              warning: 'Esta é a única vez que o token completo é exibido. Copie agora — ele não poderá ser recuperado novamente.',
              copy: 'Copiar',
              copied: 'Token copiado para a área de transferência',
              done: 'Concluído',
            },
            'revoke-confirmation': {
              title: 'Revogar esta chave de API?',
              text: 'Essa ação não pode ser desfeita. Qualquer integração usando essa chave deixará de autenticar imediatamente.',
            },
            messages: {
              'load-error': 'Erro ao carregar chaves de API',
              'create-success': 'Chave de API criada com sucesso',
              'create-error': 'Erro ao criar chave de API',
              'revoke-success': 'Chave de API revogada com sucesso',
              'revoke-error': 'Erro ao revogar chave de API',
            },
            'access-requests': {
              'new-btn': 'Solicitar acesso',
              outgoing: {
                title: 'Solicitações enviadas',
              },
              incoming: {
                title: 'Solicitações recebidas',
              },
              headers: {
                'target-client': 'Client',
                'requester-client': 'Client',
              },
              status: {
                pending: 'Pendente',
                accepted: 'Aceito',
                refused: 'Recusado',
                revoked: 'Revogado',
              },
              actions: {
                accept: 'Aceitar',
                refuse: 'Recusar',
                revoke: 'Revogar acesso',
              },
              dialog: {
                title: 'Solicitar acesso aos dados de outro client',
                key: 'Chave',
                client: 'Client',
                submit: 'Enviar solicitação',
              },
              'revoke-confirmation': {
                title: 'Revogar esse acesso?',
                text: 'O client requisitante perde acesso aos seus dados por essa chave imediatamente.',
              },
              messages: {
                'create-success': 'Solicitação enviada',
                'create-error': 'Erro ao enviar solicitação',
                'accept-success': 'Acesso concedido',
                'refuse-success': 'Solicitação recusada',
                'revoke-success': 'Acesso revogado',
                'action-error': 'Erro ao processar a solicitação',
              },
            },
          },
          investors: {
            title: 'Investidores',
            description: 'Convide um investidor para acompanhar os dados da sua empresa no Portfolio',
            'new-btn': 'Convidar investidor',
            'search-placeholder': 'Buscar por investidor ou e-mail...',
            'no-search-results': 'Nenhum investidor corresponde à busca.',
            tabs: {
              invites: 'Convites',
              modules: 'Módulos liberados',
            },
            modules: {
              dataroom: 'Dataroom',
              reports: 'Reports',
              'reports-hint': 'Esse investidor poderá acessar apenas os reports que você publicou para ele.',
            },
            headers: {
              email: 'E-mail',
              investor: 'Investidor',
              kind: 'Conta',
              status: 'Status',
              'created-at': 'Criado em',
              'expires-at': 'Expira em',
              actions: 'Ações',
            },
            kind: {
              corporate: 'Conta corporativa',
              personal: 'Conta pessoal',
            },
            status: {
              pending: 'Pendente',
              accepted: 'Aceito',
              refused: 'Recusado',
              revoked: 'Revogado',
              expired: 'Expirado',
            },
            actions: {
              revoke: 'Revogar',
            },
            dialog: {
              title: 'Convidar investidor',
              email: 'E-mail do investidor',
              'email-required': 'E-mail é obrigatório',
              'email-invalid': 'Informe um e-mail válido',
              cancel: 'Cancelar',
              submit: 'Enviar convite',
              'kind-label': 'Para que tipo de conta é este convite?',
              'kind-corporate-hint': 'Uma empresa ou fundo que tem (ou vai criar) conta própria na plataforma, com usuários e módulos próprios.',
              'kind-personal-hint': 'Uma pessoa que só vai acompanhar a sua empresa no Portfólio. Ela ganha uma conta própria e não pode convidar ninguém.',
              'custom-domain-notice': 'O link do convite vai usar o seu domínio personalizado:',
              'grant-dataroom': 'Liberar o Dataroom assim que o convite for aceito',
              'grant-dataroom-hint': 'Sem isso, você precisa voltar nesta tela depois do aceite para ligar o Dataroom.',
            },
            'revoke-confirmation': {
              title: 'Revogar este convite?',
              text: 'O investidor perderá o acesso aos seus dados na próxima requisição.',
            },
            messages: {
              'create-success': 'Convite enviado',
              'create-error': 'Erro ao enviar convite',
              'revoke-success': 'Convite revogado',
              'action-error': 'Erro ao processar o convite',
              'module-update-success': 'Módulo atualizado',
            },
          },
        },
      },
      dataIterator: {
        rowsPerPageText: 'Elementos por página:',
        pageText: '{0}-{1} de {2}',
        loadingText: 'Carregando itens...',
      },
      dataFooter: {
        itemsPerPageText: 'Itens por página:',
        pageText: '{0}-{1} de {2}',
        itemsPerPageAll: 'Todas',
        nextPage: 'Próxima Página',
        prevPage: 'Página Anterior',
        lastPage: 'Última Página',
        firstPage: 'Primeira Página',
      },
      resources: {
        menus: {
          title: 'Navegação',
        },
        dashboard: {
          title: 'Home',
          read: 'Visualizar Home',
        },
        portfolio: {
          title: 'Portfólio',
          read: 'Visualizar Portfólio',
        },
        cockpit: {
          title: 'Cockpit',
          read: 'Visualizar Cockpit',
          summary: {
            title: 'Resumo',
            read: 'Visualizar Resumo',
          },
          analysis: {
            title: 'Análise',
            read: 'Visualizar Análise',
            accounting: {
              title: 'Contábil',
              read: 'Visualizar Módulo Contábil',
            },
            financial: {
              title: 'Financeiro',
              read: 'Visualizar Módulo Financeiro',
              summary: {
                read: 'Visualizar Resumo',
              },
              receipts: {
                title: 'Recebimentos',
                read: 'Visualizar Recebimentos',
              },
              payments: {
                title: 'Pagamentos',
                read: 'Visualizar Pagamentos',
              },
              budget: {
                title: 'Orçamento',
                read: 'Visualizar Orçamento',
              },
              'cash-flow-statement': {
                title: 'Demonstração de Fluxo de Caixa',
                read: 'Visualizar Demonstração de Fluxo de Caixa',
              },
            },
            sales: {
              title: 'Comercial',
              read: 'Visualizar Módulo Comercial',
              summary: {
                read: 'Visualizar Resumo',
              },
              ticket: {
                title: 'Tickets',
                read: 'Visualizar Breakdown de Tickets',
              },
              product: {
                title: 'Portfólio de Produtos',
                read: 'Visualizar Portfólio de Produtos',
              },
              'sales-team': {
                title: 'Equipe de Vendas',
                read: 'Visualizar Equipe de Vendas',
              },
            },
            indicators: {
              title: 'Dados Complementares',
              read: 'Visualizar Módulo de Dados Complementares',
              new: 'Criar Indicador para o Mês de Referência',
              edit: 'Editar Indicador para o Mês de Referência',
              delete: 'Deletar Indicador para o Mês de Referência',
              'upload-spreadsheet': 'Upload de Planilha de Indicadores',
            },
            reports: {
              title: 'Relatórios',
              read: 'Visualizar Módulo de Relatórios',
              new: 'Criar novo relatório',
              edit: 'Editar relatório',
              delete: 'Deletar relatório',
            },
            operations: {
              title: 'Análise Operacional',
              read: 'Visualizar Módulo de Análise Operacional',
              inventory: {
                read: 'Visualizar Inventário',
              },
              summary: {
                read: 'Visualizar Resumo',
              },
            },
            funding: {
              title: 'Fundraising & IR',
              read: 'Visualizar Módulo de Fundraising & IR',
              new: 'Criar Tabela de Captação',
              edit: 'Editar Tabela de Captação',
              delete: 'Deletar Tabela de Captação',
            },
          },
          alerts: {
            title: 'Alertas',
            read: 'Visualizar Alertas',
          },
        },
        dataroom: {
          title: 'Dataroom',
          read: 'Visualizar Dataroom',
        },
        'company-area': {
          title: 'Área da Empresa',
          read: 'Visualizar Área da Empresa',
          operations: {
            title: 'Análise Operacional',
            read: 'Visualizar Operações',
          },
          sales: {
            title: 'Comercial',
            read: 'Visualizar Comercial',
          },
          analysis: {
            title: 'Análise',
            read: 'Visualizar Análise',
          },
        },
        configuration: {
          title: 'Configuração',
          read: 'Visualizar Configuração',
          modules: {
            title: 'Módulos de Configuração',
            read: 'Visualizar Módulos de Configuração',
            accounting: {
              title: 'Configuração do Módulo Contábil',
              read: 'Visualizar Configuração do Módulo Contábil',
              new: 'Criar Configuração do Módulo Contábil',
              edit: 'Editar Configuração do Módulo Contábil',
              delete: 'Deletar Configuração do Módulo Contábil',
            },
            financial: {
              title: 'Configuração do Módulo Financeiro',
              read: 'Visualizar Configuração do Módulo Financeiro',
              new: 'Criar Configuração do Módulo Financeiro',
              edit: 'Editar Configuração do Módulo Financeiro',
              delete: 'Deletar Configuração do Módulo Financeiro',
              'view-chart-accounts': 'Visualizar Plano de Contas Personalizado',
              'edit-chart-accounts': 'Editar Plano de Contas Personalizado',
            },
            sales: {
              title: 'Configuração do Módulo Comercial',
              read: 'Visualizar Configuração do Módulo Comercial',
              new: 'Criar Configuração do Módulo Comercial',
              edit: 'Editar Configuração do Módulo Comercial',
              delete: 'Deletar Configuração do Módulo Comercial',
            },
            dataroom: {
              title: 'Configuração do Módulo Dataroom',
              read: 'Visualizar Configuração do Módulo Dataroom',
              new: 'Criar Dataroom',
              edit: 'Editar Dataroom',
              delete: 'Deletar Dataroom',
            },
            indicators: {
              title: 'Configuração de Dados Complementares',
              read: 'Visualizar Configuração de Dados Complementares',
              new: 'Criar Dados Complementares',
              edit: 'Editar Dados Complementares',
              delete: 'Deletar Dados Complementares',
              'expected-scenarios': 'Gerenciar Cenários dos Dados Complementares',
            },
            'company-area': {
              title: 'Configuração da Área da Empresa',
              read: 'Visualizar Configuração da Área da Empresa',
              new: 'Criar Área da Empresa',
              edit: 'Editar Área da Empresa',
              delete: 'Deletar Área da Empresa',
            },
          },
          alerts: {
            title: 'Configuração de Alertas',
            read: 'Visualizar Configuração de Alertas',
            new: 'Criar Alerta',
            edit: 'Editar Alerta',
            delete: 'Deletar Alerta',
          },
          users: {
            title: 'Configuração de Usuários',
            read: 'Visualizar Configuração de Usuários',
            new: 'Criar Usuário',
            edit: 'Editar Usuário',
            delete: 'Deletar Usuário',
          },
          roles: {
            title: 'Configuração de Perfis de Acesso',
            read: 'Visualizar Configuração de Perfis de Acesso',
            new: 'Criar Perfil de Acesso',
            edit: 'Editar Perfil de Acesso',
            delete: 'Deletar Perfil de Acesso',
          },
        },
      },
      warning: {
        permission: {
          text: 'Você não tem permissão para acessar o conteúdo desta página. Por favor, contate seu gerente de conta.',
        },
      },
    },
  },
};

// @ts-ignore
const i18n = new createI18n({
  legacy: false, // Vuetify does not support the legacy mode of vue-i18n
  locale: 'en',
  fallbackLocale: 'en',
  messages,
});
export default i18n;
